/* Multi-page restaurant site — hero/home + menu + about + contact + cart +
 * locations. All routes are children under /r/:slug.
 *
 * The shared <RestaurantSiteLayout/> renders the sticky branded header with
 * site navigation and a footer. Each page is a small self-contained component.
 *
 * Theme: applies the restaurant's active_template CSS variables to the wrapper
 * so free/diamond palettes work site-wide.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { useWhitelabelSlug } from "@/pages/SiteRouter";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Mail,
  MapPin,
  Menu as MenuIcon,
  Phone,
  ShoppingBag,
  Sparkles,
  Star,
  X,
  Bus,
  Train,
  Ship,
  Trees,
  Wine,
  Toilet,
  Globe,
  Facebook,
  Instagram,
  Send,
  Trash2,
  Minus,
  Plus,
  AlertCircle,
  ChevronLeft,
  ChevronUp,
  LayoutGrid,
  List as ListIcon,
} from "lucide-react";

import { DemoCartProvider, useDemoCart } from "@/context/DemoCartContext";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import MenuItemDetailModal from "@/components/MenuItemDetailModal";
import OwnerEditMenuItemModal from "@/components/OwnerEditMenuItemModal";
import QuickOrderBar from "@/components/QuickOrderBar";
import UsualOrderPrompt from "@/components/UsualOrderPrompt";
import BookingChoiceModal, {PartnerPanel} from "@/components/BookingChoiceModal";
import { Pencil } from "lucide-react";

// Cuisines / sub-niches that get the prominent Quick Order Bar at top of menu.
const QUICK_ORDER_CUISINES = new Set([
  "cafe", "coffee", "coffee shop", "coffee & breakfast",
  "pizza", "pizzeria", "kebab", "turkish", "fast food", "takeaway",
  "burger", "burgers", "sandwich", "bakery", "donuts", "fish & chips",
]);
function isQuickOrder(restaurant) {
  // Admin override always wins
  if (restaurant?.show_quick_order != null) return !!restaurant.show_quick_order;
  const cuisine = (restaurant?.cuisine || "").toLowerCase();
  const sub = (restaurant?.sub_niche || "").toLowerCase();
  if (QUICK_ORDER_CUISINES.has(cuisine) || QUICK_ORDER_CUISINES.has(sub)) return true;
  return /coffee|pizza|kebab|fast|takeaway|burger|sandwich/.test(cuisine);
}
function isReserveTable(restaurant) {
  if (restaurant?.show_reserve_table != null) return !!restaurant.show_reserve_table;
  // Default: restaurants get Reserve; everything else doesn't.
  return !restaurant?.niche || restaurant.niche === "Restaurants";
}

/**
 * Smart hero CTA cluster
 * ──────────────────────
 * When a venue has BOTH Quick Order + Reserve Table (e.g. Coffee Commune —
 * coffee shop AND sit-down restaurant), we render a smaller paired toggle
 * to keep both visible. Single-CTA venues get the big bold button.
 */
function HeroCtaCluster({ restaurant, slug, isWhitelabel }) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const qo = isQuickOrder(restaurant);
  const rt = isReserveTable(restaurant);
  const externalBookUrl = restaurant.external_booking_url;
  const externalProvider = restaurant.booking_provider;
  // iter373c patch H — whitelabel-aware URL base so hero CTAs stay
  // on the vendor's root domain instead of jumping to /r/{slug}/menu.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  // If a Platinum tenant has wired an external booking system (Timely / Fresha /
  // Ovatu / Square Appointments / Calendly etc), surface a primary Book Now CTA
  // alongside any Quick Order / Reserve CTAs.
  if (externalBookUrl) {
    const label = (() => {
      switch (externalProvider) {
        case "timely": return "Book an Appointment";
        case "fresha": return "Book on Fresha";
        case "square_appointments": return "Book Now";
        case "calendly": return "Book a Call";
        default: return "Reserve a Table";
      }
    })();
    // When the venue prefers in-house booking (e.g. Magma / Cabravale
    // resort), open the BookingChoiceModal so HustleEats is offered
    // first and the partner system is embedded as a secondary path
    // INSIDE the modal — guests never leave our domain.
    const preferModal = !!restaurant.prefer_inhouse_booking
                              || externalProvider === "sevenrooms"
                              || externalProvider === "opentable"
                              || externalProvider === "resy"
                              || externalProvider === "quandoo";
    if (preferModal) {
      return (
        <div className="ml-2 inline-flex items-center gap-1.5"
                data-testid="cta-cluster-external">
          <button
            type="button"
            onClick={() => setBookingOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2
                          rounded-full text-[13px] font-bold text-white
                          hover:opacity-90"
            style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
            data-testid="cta-external-book"
          >
            {label} <ArrowRight className="w-3.5 h-3.5" />
          </button>
          {qo && (
            <Link to={`${base}/menu`}
                    className="inline-flex items-center gap-1 px-3
                                  py-1.5 rounded-full text-[12px]
                                  font-bold bg-white border-2
                                  hover:bg-stone-50"
                    style={{ borderColor: "var(--tpl-accent, #292524)",
                                color:       "var(--tpl-accent, #292524)" }}
                    data-testid="cta-quick-order">
              ⚡ Quick Order
            </Link>
          )}
          <BookingChoiceModal
            slug={slug}
            externalUrl={externalBookUrl}
            provider={externalProvider}
            venueName={restaurant.name}
            preferInhouse={true}
            open={bookingOpen}
            onClose={() => setBookingOpen(false)} />
        </div>
      );
    }
    return (
      <div className="ml-2 inline-flex items-center gap-1.5" data-testid="cta-cluster-external">
        <a
          href={externalBookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
          style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
          data-testid="cta-external-book"
        >
          {label} <ArrowRight className="w-3.5 h-3.5" />
        </a>
        {qo && (
          <Link
            to={`${base}/menu`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white border-2 hover:bg-stone-50"
            style={{ borderColor: "var(--tpl-accent, #292524)", color: "var(--tpl-accent, #292524)" }}
            data-testid="cta-quick-order"
          >
            ⚡ Quick Order
          </Link>
        )}
      </div>
    );
  }
  if (!qo && !rt) {
    // Non-Restaurants: point the primary CTA at the vendor's contact/enquiry
    // form instead of the (never-populated) menu.
    if (restaurant.niche && restaurant.niche !== "Restaurants") {
      return (
        <Link
          to={`${base}/contact#enquiry`}
          className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
          style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
          data-testid="cta-contact"
        >
          Send an enquiry <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      );
    }
    // Restaurants fallback — offer "View Menu"
    return (
      <Link
        to={`${base}/menu`}
        className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
        style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
        data-testid="cta-menu"
      >
        View menu <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    );
  }
  if (qo && rt) {
    // Hybrid venue — show paired smaller buttons
    return (
      <div className="ml-2 inline-flex items-center gap-1.5" data-testid="cta-cluster-hybrid">
        <Link
          to={`${base}/menu?intent=quick`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold text-white hover:opacity-90 shadow-sm"
          style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
          data-testid="cta-quick-order"
        >
          ⚡ Quick Order
        </Link>
        <Link
          to={`${base}/reserve`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white border-2 hover:bg-stone-50"
          style={{ borderColor: "var(--tpl-accent, #292524)", color: "var(--tpl-accent, #292524)" }}
          data-testid="cta-reserve-table"
        >
          🍽️ Reserve
        </Link>
      </div>
    );
  }
  // Single CTA
  if (qo) {
    return (
      <Link
        to={`${base}/menu`}
        className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
        style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
        data-testid="cta-quick-order"
      >
        ⚡ Order Online <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    );
  }
  return (
    <Link
      to={`${base}/reserve`}
      className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
      style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
      data-testid="cta-reserve-table"
    >
      🍽️ Reserve a Table <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  );
}

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const fmt = (n) => Number(n || 0).toFixed(2);

// ─── Theme helpers ─────────────────────────────────────────────────────────
function useRestaurant(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/directory/restaurants/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);
  return { data, loading };
}

function themeStyle(active_template) {
  if (!active_template || !active_template.stylesheet) return null;
  return {
    ...active_template.stylesheet.css_vars,
    backgroundColor: "var(--tpl-bg, #fafaf9)",
    color: "var(--tpl-ink, #1c1917)",
    fontFamily: "var(--tpl-font-body, system-ui)",
  };
}

// ─── Scroll-to-top FAB ─────────────────────────────────────────────────────
// iter374 patch M11 (2026-02-13) — restores the "top-of-page" button
// the user reported missing. Appears once the visitor has scrolled past
// 300px; sits ~80px above the viewport bottom so it never collides with
// the floating <CartPill/>.
function ScrollToTopFab() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top of page"
      title="Back to top"
      data-testid="scroll-to-top-fab"
      className="fixed bottom-20 right-5 z-40 inline-flex items-center justify-center w-11 h-11 rounded-full shadow-xl bg-stone-900 text-white hover:bg-stone-700 transition-all"
    >
      <ChevronUp className="w-5 h-5" />
    </button>
  );
}

// ─── Cart pill (floating) ──────────────────────────────────────────────────
function CartPill({ slug, restaurantId, isWhitelabel }) {
  const { itemCount, subtotal } = useDemoCart(restaurantId);
  // iter373c patch H — base URL respects whitelabel mode so the cart
  // pill navigates within the vendor domain root instead of /r/{slug}.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  if (itemCount === 0) return null;
  return (
    <Link
      to={`${base}/cart`}
      data-testid="floating-cart-pill"
      className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full shadow-xl bg-stone-900 text-white text-[14px] font-semibold hover:bg-stone-700"
    >
      <ShoppingBag className="w-4 h-4" /> {itemCount} item{itemCount > 1 ? "s" : ""} · ${fmt(subtotal)}
      <ChevronRight className="w-3.5 h-3.5" />
    </Link>
  );
}

// ─── Site Header ───────────────────────────────────────────────────────────
function SiteHeader({ restaurant, slug, isWhitelabel }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [nearbySuburbs, setNearbySuburbs] = useState([]);
  // iter373c patch G — base URL for every internal NavLink/Link. On a
  // whitelabel host (or when the vendor has toggled `hide_hustle_header`)
  // links resolve at the root: `/menu`, `/pages/tax-savings`, `/contact`.
  // On the shared HustleDirectory host they stay namespaced under
  // `/r/{slug}` so multiple vendors coexist.
  const base = isWhitelabel ? "" : `/r/${slug}`;

  useEffect(() => {
    fetch(`${API}/api/directory/restaurants/${slug}/nearby-suburbs`)
      .then((r) => r.json())
      .then((d) => setNearbySuburbs(d.suburbs || []))
      .catch(() => {});
  }, [slug]);

  const linkClass = ({ isActive }) =>
    `px-3 py-1.5 text-[13px] font-semibold rounded-md transition-colors ${
      isActive ? "bg-stone-900 text-white" : "text-stone-700 hover:bg-stone-100"
    }`;

  // iter374 patch M11.1 (2026-02-13) — On whitelabel hosts the slim
  // black <WhitelabelTopBar/> is also `sticky top-0` at z-[70]. If we
  // stay at `top-0` the yellow branded header slides *under* the black
  // bar on scroll. Measure the black bar's ACTUAL rendered height with
  // useLayoutEffect so we're pixel-perfect regardless of font, border,
  // or line-height changes. Falls back to 29px (28 content + 1 border)
  // before the first paint.
  const [wlOffset, setWlOffset] = useState(isWhitelabel ? 29 : 0);
  useLayoutEffect(() => {
    if (!isWhitelabel) return undefined;
    const measure = () => {
      const bar = document.querySelector('[data-testid="whitelabel-top-bar"]');
      if (bar) {
        const h = Math.round(bar.getBoundingClientRect().height);
        if (h > 0) setWlOffset(h);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isWhitelabel]);

  return (
    <header
      className="sticky z-30 border-b backdrop-blur"
      style={{
        top: `${wlOffset}px`,
        backgroundColor: "color-mix(in srgb, var(--tpl-surface, #fff) 92%, transparent)",
        borderColor: "var(--tpl-line, #e7e5e4)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        {!isWhitelabel && (
          <Link
            to="/directory"
            data-testid="back-to-directory"
            className="hidden sm:inline-flex items-center gap-1 text-[12px] font-semibold text-stone-500 hover:text-stone-900 transition-colors mr-1"
            aria-label="Back to Hustle Directory search"
            title="Back to Hustle Directory search"
          >
            <ChevronLeft className="w-4 h-4" />
            Directory
          </Link>
        )}
        <Link to={base || "/"} className="flex items-center gap-2.5 mr-2">
          {restaurant.logo ? (
            <img src={restaurant.logo} alt="" className="w-9 h-9 rounded-full object-cover bg-stone-200" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold"
                 style={{ backgroundColor: "var(--tpl-accent, #292524)" }}>
              {restaurant.name?.[0] || "R"}
            </div>
          )}
          <span
            className="font-bold text-[16px] tracking-tight hidden sm:inline"
            style={{ fontFamily: "var(--tpl-font-display, serif)" }}
          >
            {restaurant.name}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-auto">
          <NavLink to={`${base || "/"}`} end className={linkClass} data-testid="nav-home">Home</NavLink>
          {(!restaurant.niche || restaurant.niche === "Restaurants" || restaurant.menu_label === "Products") && (
            restaurant.menu_label === "Products" ? (
            <div className="relative group" data-testid="nav-products-group">
              <NavLink to={`${base}/menu`} className={linkClass} data-testid="nav-menu">
                <span className="inline-flex items-center gap-0.5">
                  Products <ChevronDown className="w-3 h-3" />
                </span>
              </NavLink>
              <div className="absolute left-0 top-full pt-1 w-44 hidden group-hover:block z-20">
                <div className="bg-white rounded-lg shadow-xl border border-stone-200 py-1.5">
                  <Link
                    to={`${base}/menu`}
                    data-testid="nav-products-treatments"
                    className="block px-3.5 py-1.5 text-[13px] hover:bg-stone-50 text-stone-700"
                  >
                    Treatments
                  </Link>
                  <Link
                    to={`${base}/aftercare`}
                    data-testid="nav-products-aftercare"
                    className="block px-3.5 py-1.5 text-[13px] hover:bg-stone-50 text-stone-700"
                  >
                    Aftercare
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <NavLink to={`${base}/menu`} className={linkClass} data-testid="nav-menu">
              {restaurant.menu_label || "Menu"}
            </NavLink>
          ))}
          {/* iter364: Business Menu dropdown — non-Restaurants vendors
              expose their service pages here (Tax Savings, Company Tax,
              SMSF, TPAR, CGT, …). Matches the SSR ``_business_menu_html``. */}
          {(restaurant.niche && restaurant.niche !== "Restaurants" && (restaurant.service_pages || []).length > 0) && (
            <div className="relative group" data-testid="nav-business-menu-group">
              <button
                type="button"
                className={linkClass({ isActive: false })}
                data-testid="nav-business-menu"
              >
                <span className="inline-flex items-center gap-0.5">
                  Business Menu <ChevronDown className="w-3 h-3" />
                </span>
              </button>
              <div className="absolute left-0 top-full pt-1 w-64 hidden group-hover:block z-20">
                    <div className="bg-white dark:bg-stone-900 rounded-lg shadow-xl border border-stone-200 dark:border-stone-700 py-1.5 max-h-[70vh] overflow-y-auto">
                  {(restaurant.service_pages || []).map((sp) => (
                    <Link
                      key={sp.slug}
                      to={`${base}/business/${sp.slug}`}
                      data-testid={`nav-business-menu-${sp.slug}`}
                      className="block px-3.5 py-1.5 text-[13px] hover:bg-stone-50 text-stone-700"
                    >
                      {sp.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
          <NavLink to={`${base}/about`} className={linkClass} data-testid="nav-about">About</NavLink>
          <NavLink to={`${base}/contact`} className={linkClass} data-testid="nav-contact">Contact</NavLink>
          {restaurant.external_booking_url && <NavLink to={base+"/book-appointment"} className={linkClass} data-testid="nav-book-appointment">Book an Appointment</NavLink>}
          {(restaurant.tier === "platinum" || restaurant.is_claimed) &&
           (!restaurant.niche || restaurant.niche === "Restaurants") && (
            <div className="relative group">
              <button
                className="inline-flex items-center gap-0.5 px-3 py-1.5 text-[13px] font-semibold rounded-md text-stone-700 hover:bg-stone-100"
                data-testid="nav-more"
              >
                More <ChevronDown className="w-3 h-3" />
              </button>
              <div className="absolute right-0 top-full pt-1 w-44 hidden group-hover:block">
                <div className="bg-white rounded-lg shadow-xl border border-stone-200 py-1.5">
                  {[
                    ["aftercare", "Aftercare"],
                    ["gallery", "Gallery"],
                    ["functions", "Functions"],
                    ["catering", "Catering"],
                    ["reviews", "Reviews"],
                    ["faq", "FAQ"],
                    ["specials", "Specials"],
                    ["gift-cards", "Gift Cards"],
                    ["loyalty", "Loyalty"],
                    ["press", "Press"],
                    ["blog", "Blog"],
                  ].map(([k, lbl]) => (
                    <Link
                      key={k}
                      to={`${base}/${k}`}
                      data-testid={`nav-paid-${k}`}
                      className="block px-3.5 py-1.5 text-[13px] hover:bg-stone-50 text-stone-700"
                    >
                      {lbl}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="relative" onMouseEnter={() => setLocOpen(true)} onMouseLeave={() => setLocOpen(false)}>
            <button
              className="inline-flex items-center gap-0.5 px-3 py-1.5 text-[13px] font-semibold rounded-md text-stone-700 hover:bg-stone-100"
              data-testid="nav-locations"
            >
              Locations <ChevronDown className="w-3 h-3" />
            </button>
            {locOpen && nearbySuburbs.length > 0 && (
              <div className="absolute right-0 top-full pt-1 w-56">
                <div className="bg-white rounded-lg shadow-xl border border-stone-200 py-1.5">
                  {nearbySuburbs.map((s) => (
                    <Link
                      key={s.slug}
                      to={`${base}/locations/${s.slug}`}
                      data-testid={`loc-link-${s.slug}`}
                      className="block px-3.5 py-1.5 text-[13px] hover:bg-stone-50 text-stone-700"
                    >
                      {restaurant.cuisine
                        ? `${restaurant.cuisine} in ${s.name}`
                        : restaurant.niche && restaurant.niche !== "Restaurants"
                          ? `${restaurant.name} serves ${s.name}`
                          : `Restaurants in ${s.name}`}
                      {s.restaurant_count != null && (
                        <span className="text-[10px] text-stone-400 ml-1">({s.restaurant_count})</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <HeroCtaCluster restaurant={restaurant} slug={slug} isWhitelabel={isWhitelabel} />
        </nav>

        {/* Mobile burger */}
        <button
          className="md:hidden ml-auto p-2 rounded-md hover:bg-stone-100"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-toggle"
        >
          <MenuIcon className="w-5 h-5 text-stone-700" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setMobileOpen(false)}>
          <div
            className="absolute right-0 top-0 bottom-0 w-64 bg-white p-4 space-y-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setMobileOpen(false)} className="ml-auto block p-1">
              <X className="w-4 h-4 text-stone-500" />
            </button>
            <NavLink to={`${base || "/"}`} end className={linkClass} onClick={() => setMobileOpen(false)}>Home</NavLink>
            {(!restaurant.niche || restaurant.niche === "Restaurants" || restaurant.menu_label === "Products") && (
              <NavLink to={`${base}/menu`} className={linkClass} onClick={() => setMobileOpen(false)}>{restaurant.menu_label || "Menu"}</NavLink>
            )}
            <NavLink to={`${base}/about`} className={linkClass} onClick={() => setMobileOpen(false)}>About</NavLink>
            <NavLink to={`${base}/contact`} className={linkClass} onClick={() => setMobileOpen(false)}>Contact</NavLink>
            {restaurant.external_booking_url && <NavLink to={base+"/book-appointment"} className={linkClass} onClick={() => setMobileOpen(false)}>Book an Appointment</NavLink>}
            {nearbySuburbs.length > 0 && (
              <div className="pt-2 mt-2 border-t border-stone-200">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-stone-400 font-bold">Locations</div>
                {nearbySuburbs.map((s) => (
                  <Link
                    key={s.slug}
                    to={`${base}/locations/${s.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-1.5 text-[13px] text-stone-700 hover:bg-stone-50 rounded-md"
                  >
                    {restaurant.cuisine
                      ? `${restaurant.cuisine} in ${s.name}`
                      : restaurant.niche && restaurant.niche !== "Restaurants"
                        ? s.name
                        : `Restaurants in ${s.name}`}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

// ─── Site Footer ───────────────────────────────────────────────────────────
function SiteFooter({ restaurant, isWhitelabel }) {
  const isRestaurantsNiche = !restaurant.niche || restaurant.niche === "Restaurants";
  return (
    <footer
      className="mt-16 py-8 border-t"
      style={{ borderColor: "var(--tpl-line, #e7e5e4)", backgroundColor: "var(--tpl-surface, #fff)" }}
    >
      <div className="max-w-6xl mx-auto px-4 grid sm:grid-cols-3 gap-6 text-[13px]">
        <div>
          <div className="font-bold mb-1" style={{ fontFamily: "var(--tpl-font-display, serif)", fontSize: "18px" }}>
            {restaurant.name}
          </div>
          <p className="opacity-70">{restaurant.short_description || restaurant.business_description}</p>
        </div>
        <div className="opacity-80 space-y-1">
          {restaurant.address && (
            <div className="flex items-start gap-1.5" data-testid="footer-address"><MapPin className="w-3.5 h-3.5 mt-0.5" /> {restaurant.address}</div>
          )}
          {restaurant.phone && (
            <div className="flex items-center gap-1.5" data-testid="footer-phone"><Phone className="w-3.5 h-3.5" /> <a href={`tel:${restaurant.phone}`} className="hover:underline">{restaurant.phone}</a></div>
          )}
          {restaurant.mobile && (
            <div className="flex items-center gap-1.5" data-testid="footer-mobile"><Phone className="w-3.5 h-3.5" /> <a href={`tel:${restaurant.mobile}`} className="hover:underline">{restaurant.mobile}</a><span className="text-[10px] opacity-60">mobile</span></div>
          )}
        </div>
        <div className="text-[12px] opacity-60">
          <p>© {new Date().getFullYear()} {restaurant.name}</p>
          {!isWhitelabel && (
            <p className="mt-2">Powered by <a href="/directory" className="underline">HustleDirectory</a></p>
          )}
          {!isWhitelabel && !restaurant.is_claimed && isRestaurantsNiche && (
            <Link
              to={`/claim-business?slug=${restaurant.slug}`}
              className="inline-block mt-2 underline font-semibold"
              data-testid="footer-claim"
            >
              Is this your business? Claim it →
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────────
function SiteLayout() {
  // iter373c patch F — whitelabel host mode renders this layout at "/"
  // with no `:slug` URL param. Fall back to the slug published by
  // SiteRouter via WhitelabelSlugContext.
  const params = useParams();
  const forced = useWhitelabelSlug();
  const slug = params.slug || forced;
  const { data: restaurant, loading } = useRestaurant(slug);
  const wrapperStyle = useMemo(() => themeStyle(restaurant?.active_template), [restaurant]);
  // iter373c patch G — whitelabel mode == no HustleDirectory chrome.
  // Detect from either the vendor's toggle (`hide_hustle_header` on the
  // directory doc) or the whitelabel host mapping.
  const isWhitelabel = !!forced || !!(restaurant && restaurant.hide_hustle_header);

  if (loading) return <div className="p-12 text-center text-stone-500">Loading…</div>;
  if (!restaurant) return <div className="p-12 text-center text-stone-500">Restaurant not found.</div>;

  return (
    <div className="min-h-screen" style={wrapperStyle || undefined} data-testid="restaurant-site">
      <SiteHeader restaurant={restaurant} slug={slug} isWhitelabel={isWhitelabel} />
      <main className="min-h-[60vh]">
        <Outlet context={{ restaurant, slug, isWhitelabel }} />
      </main>
      <SiteFooter restaurant={restaurant} isWhitelabel={isWhitelabel} />
      <CartPill slug={slug} restaurantId={restaurant.id} isWhitelabel={isWhitelabel} />
      <ScrollToTopFab />
    </div>
  );
}

import PaidPage from "@/pages/PaidPage";

export default function RestaurantSite() {
  return (
    <DemoCartProvider>
      <Routes>
        <Route path="/" element={<SiteLayout />}>
          <Route index element={<HomePage />} />
          <Route path="menu" element={<MenuPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="book-appointment" element={<AppointmentPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="locations/:suburb_slug" element={<LocationPage />} />
          {/* iter364: vendor-supplied service pages (Tax Savings, Company
              Tax, SMSF, etc.). Same content as the SSR /pub/{slug}/{page}
              route — rendered here so the SPA header can deep-link into
              them via the Business Menu.
              iter374 patch M (2026-09-23) — canonical URL is `/business/*`
              per client request. Legacy `/pages/*` kept as an alias so
              old printed QRs, sitemap URLs and cached Google results
              still resolve. */}
          <Route path="business/:page_slug" element={<ServicePage />} />
          <Route path="pages/:page_slug"    element={<ServicePage />} />
          {/* 10+ pages pack for paying members */}
          <Route path="aftercare"  element={<PaidPage pageType="aftercare" />} />
          <Route path="functions"  element={<PaidPage pageType="functions" />} />
          <Route path="catering"   element={<PaidPage pageType="catering" />} />
          <Route path="gallery"    element={<PaidPage pageType="gallery" />} />
          <Route path="faq"        element={<PaidPage pageType="faq" />} />
          <Route path="reviews"    element={<PaidPage pageType="reviews" />} />
          <Route path="press"      element={<PaidPage pageType="press" />} />
          <Route path="loyalty"    element={<PaidPage pageType="loyalty" />} />
          <Route path="gift-cards" element={<PaidPage pageType="gift-cards" />} />
          <Route path="specials"   element={<PaidPage pageType="specials" />} />
          <Route path="blog"       element={<PaidPage pageType="blog" />} />
        </Route>
      </Routes>
    </DemoCartProvider>
  );
}

// ─── Helpers shared by pages ───────────────────────────────────────────────
import { useOutletContext } from "react-router-dom";
function useSite() {
  return useOutletContext();
}

// ─── Single-open FAQ accordion row ─────────────────────────────────
// iter374 patch K: replaces the native `<details>` element so only ONE
// question can be expanded at a time. Opening a different row auto-
// collapses the previously open one. Styling stays identical to the
// original bordered card look.
function FaqRow({ index, faq, open, onToggle }) {
  return (
    <div
      data-testid={`service-faq-${index}`}
      data-open={open ? "1" : "0"}
      className="rounded-lg border px-4 py-3"
      style={{ borderColor: "var(--tpl-line)" }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`service-faq-answer-${index}`}
        className="w-full text-left cursor-pointer font-semibold text-[15px] flex items-center justify-between gap-2 bg-transparent border-0 p-0"
        data-testid={`service-faq-toggle-${index}`}
      >
        <span>{faq.q}</span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform opacity-60 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p
          id={`service-faq-answer-${index}`}
          className="mt-2 text-[14px] leading-relaxed opacity-85"
          data-testid={`service-faq-answer-${index}`}
        >
          {faq.a}
        </p>
      )}
    </div>
  );
}

// ─── Vendor Service Page ───────────────────────────────────────────
// Renders one entry from `restaurant.service_pages` — same content the
// SSR `/pub/{slug}/{page-slug}` route serves, formatted for the SPA.
// Body is plain text with blank-line-separated paragraphs; the first
// line of each block is treated as a section heading when it fits the
// "Title-Case Sentence Without Full Stop" pattern.
function ServicePage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  const { page_slug } = useParams();
  const page = (restaurant.service_pages || []).find((p) => p.slug === page_slug);
  // iter373c patch H — base URL respects whitelabel mode.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  // iter374 patch K — accordion state: exactly one FAQ open at a time.
  // Opening a different row auto-closes the previous. -1 means all
  // closed on first paint (matches the shipped "collapsed by default"
  // pattern from the original <details> element).
  const [openFaq, setOpenFaq] = useState(-1);

  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 text-center" data-testid="service-page-404">
        <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-1">Page not found</p>
        <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
          We don't have that page yet
        </h1>
        <p className="text-[14px] opacity-70 mb-6">
          The service page "<code>{page_slug}</code>" hasn't been published for {restaurant.name}.
        </p>
        <Link
          to={`${base || "/"}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
          style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
        >
          Back to {restaurant.name}
        </Link>
      </div>
    );
  }

  const blocks = (page.body || "").split(/\n\n+/).map((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const lines = trimmed.split("\n");
    const first = lines[0].trim();
    const rest = lines.slice(1).join(" ").trim();
    const isHeading = first.length > 0 && first.length <= 80 && !/[.!?]$/.test(first) && rest.length > 0;
    return isHeading ? { heading: first, paragraph: rest } : { heading: null, paragraph: trimmed };
  }).filter(Boolean);

  // iter374 patch M2 — when the vendor supplies rich HTML in `body_html`
  // (from the client-signed-off .docx re-ingest), render it verbatim so
  // H2/H3/UL/OL/FAQ mark-up is preserved. Falls back to the plain-text
  // block-split renderer for every other listing.
  const bodyHtml = page.body_html || "";

  const siblings = (restaurant.service_pages || []).filter((p) => p.slug !== page_slug).slice(0, 8);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="service-page">
      <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-1" data-testid="service-page-eyebrow">
        {page.name || "Service"}
      </p>
      <h1
        className="text-3xl md:text-4xl font-bold mb-3 leading-tight"
        style={{ fontFamily: "var(--tpl-font-display, serif)" }}
        data-testid="service-page-headline"
      >
        {page.headline || page.name}
      </h1>
      {page.summary && (
        <p className="text-[16px] leading-relaxed opacity-80 mb-8 max-w-3xl" data-testid="service-page-summary">
          {page.summary}
        </p>
      )}

      {page.hero_url && (
        <div className="rounded-2xl overflow-hidden mb-8">
          <img
            src={page.hero_url}
            alt={page.headline || page.name}
            className="w-full h-56 md:h-72 object-cover"
            data-testid="service-page-hero"
          />
        </div>
      )}

      <div className="prose max-w-none space-y-5 text-[15.5px] leading-[1.75] text-stone-800">
        {bodyHtml ? (
          <div
            data-testid="service-page-body-html"
            className="hp-rich"
            style={{ lineHeight: 1.75 }}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        ) : (
          blocks.map((b, i) => (
          <div key={i} data-testid={`service-page-block-${i}`}>
            {b.heading && (
              <h2 className="text-xl md:text-2xl font-bold mt-6 mb-2" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
                {b.heading}
              </h2>
            )}
            <p className="whitespace-pre-wrap">{b.paragraph}</p>
          </div>
          ))
        )}
      </div>

      {page.faqs && page.faqs.length > 0 && (
        <div className="mt-12 pt-8 border-t" style={{ borderColor: "var(--tpl-line)" }} data-testid="service-page-faqs">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
            Frequently asked
          </h2>
          <div className="space-y-2">
            {page.faqs.map((f, i) => (
              <FaqRow
                key={i}
                index={i}
                faq={f}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 pt-8 border-t flex flex-wrap items-center gap-3" style={{ borderColor: "var(--tpl-line)" }} data-testid="service-page-cta">
        <Link
          to={`${base}/contact#enquiry`}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold text-white hover:opacity-90"
          style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
          data-testid="service-page-enquiry-btn"
        >
          Send an enquiry <ArrowRight className="w-4 h-4" />
        </Link>
        {restaurant.phone && (
          <a
            href={`tel:${restaurant.phone}`}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold border-2 hover:bg-stone-50"
            style={{ borderColor: "var(--tpl-accent, #292524)", color: "var(--tpl-accent, #292524)" }}
            data-testid="service-page-call-btn"
          >
            <Phone className="w-4 h-4" /> Call {restaurant.phone}
          </a>
        )}
      </div>

      {siblings.length > 0 && (
        <div className="mt-12 pt-8 border-t" style={{ borderColor: "var(--tpl-line)" }} data-testid="service-page-siblings">
          <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-3">Other services</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`${base}/business/${s.slug}`}
                className="rounded-lg border px-3 py-2 text-[14px] font-semibold text-stone-800 dark:text-stone-100 hover:border-emerald-600 hover:bg-emerald-50 hover:text-emerald-800 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-300 transition-colors"
                style={{ borderColor: "var(--tpl-line)" }}
                data-testid={`service-sibling-${s.slug}`}
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// HOME PAGE
// ============================================================
function HomePage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  // iter373c patch H — whitelabel-aware URL base.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  const [recipes, setRecipes] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/directory/restaurants/${slug}/recipe-library?limit=8`)
      .then((r) => r.json())
      .then((d) => setRecipes(d.recipes || []))
      .catch(() => {});
  }, [slug]);

  const seoContent = restaurant.aeo_content || restaurant.ai_site_description || restaurant.business_description || "";
  const heroImg =
    restaurant.hero ||
    restaurant.hero_image_url ||
    restaurant.aeo_feature_image ||
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600&q=80";

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[55vh] sm:h-[65vh] min-h-[420px] flex items-end">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 pb-10 text-white w-full">
          <div className="flex items-center gap-2 mb-2">
            {restaurant.rating && (
              <span className="inline-flex items-center gap-1 text-[12px] bg-white/15 backdrop-blur px-2 py-0.5 rounded-full border border-white/30">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                {restaurant.rating} <span className="opacity-70">({restaurant.review_count || 0})</span>
              </span>
            )}
            {restaurant.active_template && (
              <span className="inline-flex items-center gap-1 text-[12px] bg-white/15 backdrop-blur px-2 py-0.5 rounded-full border border-white/30">
                <Sparkles className="w-3 h-3" /> {restaurant.active_template.name}
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight max-w-2xl"
              style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
            {restaurant.name}
          </h1>
          <p className="text-base sm:text-lg mt-2 opacity-90 max-w-xl">
            {restaurant.short_description || restaurant.ai_summary?.slice(0, 140) ||
              (restaurant.suburb
                ? `${restaurant.cuisine || "Local"} in ${restaurant.suburb}.`
                : (restaurant.cuisine || restaurant.sub_niche || "Independent business")
              )}
          </p>
          <div className="mt-5 flex gap-2 flex-wrap">
            {restaurant.external_booking_url ? (
              <a
                href={restaurant.external_booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--tpl-accent, #c2410c)" }}
                data-testid="hero-cta-book"
              >
                Book an Appointment <ArrowRight className="w-4 h-4" />
              </a>
            ) : restaurant.niche && restaurant.niche !== "Restaurants" && restaurant.menu_label !== "Products" ? (
              <Link
                to={`${base}/contact#enquiry`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--tpl-accent, #c2410c)" }}
                data-testid="hero-cta-contact"
              >
                Send an enquiry <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <Link
                to={`${base}/menu`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--tpl-accent, #c2410c)" }}
                data-testid="hero-cta-menu"
              >
                {(restaurant.menu_label || "Menu") === "Products"
                  ? "View Our Services"
                  : (restaurant.niche === "Restaurants" ? "View Menu & Order" : "View Products")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link
              to={`${base}/contact`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold bg-white/10 backdrop-blur border border-white/30 text-white hover:bg-white/20"
            >
              <MapPin className="w-4 h-4" /> Find Us
            </Link>
          </div>
        </div>
      </section>

      {/* iter374 patch M — Owner/Principal spotlight card for professional
          services (accountants, medical, legal). Shows the primary contact's
          headshot + intro pulled from `restaurant.primary_contact_photo`,
          `primary_contact_name` and `primary_contact_intro`. Renders only
          when both photo and name exist so the section stays invisible
          for tenants that haven't uploaded a headshot yet. */}
      {restaurant.primary_contact_photo && restaurant.primary_contact_name && (
        <section
          className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-[240px_1fr] gap-6 md:gap-10 items-center"
          data-testid="home-owner-spotlight"
        >
          <div className="flex justify-center md:justify-start">
            <div
              className="w-40 h-40 md:w-56 md:h-56 rounded-full overflow-hidden ring-4 ring-white shadow-xl"
              style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.35)" }}
            >
              <img
                src={restaurant.primary_contact_photo}
                alt={restaurant.primary_contact_name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-bold opacity-60 mb-2">
              Meet Your Local {restaurant.niche || "Accountant"}
            </p>
            <h2
              className="text-2xl sm:text-3xl font-bold mb-3 leading-tight"
              style={{ fontFamily: "var(--tpl-font-display, serif)" }}
            >
              Hi! I&apos;m {restaurant.primary_contact_name.split(" ")[0]}, your local
              {" "}{(restaurant.primary_contact_role || "accountant").toLowerCase()}
              {restaurant.suburb ? ` in ${restaurant.suburb}.` : "."}
            </h2>
            {restaurant.primary_contact_intro && (
              <p className="text-[15px] leading-relaxed opacity-85 mb-4">
                {restaurant.primary_contact_intro}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {restaurant.phone && (
                <a
                  href={`tel:${restaurant.phone}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--tpl-accent, #c2410c)" }}
                  data-testid="owner-cta-call"
                >
                  Call {restaurant.phone}
                </a>
              )}
              <Link
                to={`${base}/contact#enquiry`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold border-2 hover:opacity-90"
                style={{ borderColor: "var(--tpl-accent, #c2410c)", color: "var(--tpl-accent, #c2410c)" }}
                data-testid="owner-cta-enquiry"
              >
                Book Free 20-Min Consultation
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* iter374 patch M — Team grid (accountants, agencies, medical,
          hospitality). Renders the `restaurant.team` array so each team
          member gets a headshot + role + short bio card. Hidden when
          no team data is present. */}
      {Array.isArray(restaurant.team) && restaurant.team.length > 0 && (
        <section
          className="max-w-6xl mx-auto px-4 py-12 border-t"
          style={{ borderColor: "var(--tpl-line)" }}
          data-testid="home-team"
        >
          <p className="text-[11px] uppercase tracking-[0.24em] font-bold opacity-60 mb-2">
            Our Team
          </p>
          <h2
            className="text-2xl sm:text-3xl font-bold mb-6 leading-tight"
            style={{ fontFamily: "var(--tpl-font-display, serif)" }}
          >
            Meet the {restaurant.suburb || "Local"} Team
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {restaurant.team.map((member, i) => (
              <div
                key={member.name || i}
                className="text-center"
                data-testid={`team-member-${i}`}
              >
                {member.photo_url && (
                  <div className="mx-auto w-32 h-32 rounded-full overflow-hidden mb-3 ring-2 ring-white shadow-lg">
                    <img
                      src={member.photo_url}
                      alt={member.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <h3 className="font-bold text-[15px]">{member.name}</h3>
                {member.role && (
                  <p className="text-[13px] opacity-70 mt-0.5">{member.role}</p>
                )}
                {member.qualifications && (
                  <p className="text-[11px] opacity-60 mt-1">{member.qualifications}</p>
                )}
                {member.bio && (
                  <p className="text-[12px] opacity-80 mt-2 leading-relaxed">{member.bio}</p>
                )}
                {Array.isArray(member.languages) && member.languages.length > 0 && (
                  <p className="text-[11px] mt-2 opacity-60 uppercase tracking-wider">
                    {member.languages.join(" · ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AEO content — iter374 patch M4: prefer rich `aeo_content_html`
          (H2/H3, lists, FAQ Q&A parsed verbatim from the client .docx)
          so the SPA homepage matches the SSR pub page. */}
      <section className="max-w-3xl mx-auto px-4 py-12 space-y-5 prose-stone" data-testid="home-aeo">
        {restaurant.enrichment_status && restaurant.enrichment_status !== "ready" && !seoContent && !restaurant.aeo_content_html && (
          <div className="rounded-2xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6" data-testid="home-curating-banner">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wider mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              AI Building Your Homepage
            </div>
            <p className="text-[15px] text-stone-800 leading-relaxed">
              <span className="font-bold">{restaurant.name}</span>'s site went live moments ago.
              Our AI is now reading {restaurant.claim_source_url ? "your existing WordPress site" : "your homepage"},
              generating SEO-optimised copy, sourcing imagery, and drafting the
              About / Functions / Catering / Gallery / FAQ / Reviews / Specials pages.
              The full polished site will be ready within{" "}
              <span className="font-bold">{restaurant.enrichment_eta_days || 7} days</span>.
              {restaurant.external_booking_url && (
                <>
                  {" "}Meanwhile, your <a href={restaurant.external_booking_url} target="_blank" rel="noopener noreferrer" className="font-bold underline">booking system is already wired in</a>.
                </>
              )}
            </p>
          </div>
        )}
        {restaurant.aeo_content_html ? (
          <div
            data-testid="home-aeo-body-html"
            className="hp-rich"
            style={{ lineHeight: 1.75 }}
            dangerouslySetInnerHTML={{ __html: restaurant.aeo_content_html }}
          />
        ) : (
          <RichContent text={seoContent} />
        )}
      </section>

      {/* Cuisine-matched recipes — restaurants only */}
      {restaurant.niche === "Restaurants" && recipes.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-8" data-testid="home-recipes">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold">From our recipe library</p>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
                Recipes you might love
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {recipes.slice(0, 8).map((r, i) => (
              <div key={r.slug} className="rounded-xl overflow-hidden bg-stone-100" data-testid={`home-recipe-${r.slug}`}>
                <div className="aspect-[4/3] relative overflow-hidden">
                  {r.image_url ? (
                    <img
                      src={r.image_url}
                      alt={r.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // Gradient + initial fallback when image is 403/broken
                        const parent = e.target.parentElement;
                        if (parent && !parent.dataset.fallback) {
                          parent.dataset.fallback = "1";
                          const hue = (r.title || "").charCodeAt(0) * 7 % 360;
                          parent.style.background = `linear-gradient(135deg, hsl(${hue}, 30%, 75%), hsl(${(hue+40)%360}, 35%, 55%))`;
                          parent.innerHTML = `<div class="absolute inset-0 flex items-center justify-center text-white text-3xl font-bold opacity-80" style="font-family: var(--tpl-font-display, serif)">${(r.title || '').charAt(0)}</div>`;
                        }
                      }}
                    />
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center text-white text-3xl font-bold opacity-80"
                      style={{
                        fontFamily: "var(--tpl-font-display, serif)",
                        background: `linear-gradient(135deg, hsl(${(i*47)%360}, 30%, 70%), hsl(${(i*47+50)%360}, 40%, 55%))`,
                      }}
                    >
                      {(r.title || "").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[12px] font-semibold leading-tight capitalize line-clamp-2">{r.title?.toLowerCase()}</div>
                  <div className="text-[10px] opacity-50 mt-0.5">{r.cuisine} · {r.course}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Local images strip — uses entity's gallery_images when available,
          falls back to generic food shots only for Restaurants. */}
      {(() => {
        const ownImgs = restaurant.gallery_images || [];
        const restaurantFallback = [
          "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=70",
          "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=70",
          "https://images.unsplash.com/photo-1485921325833-c519f76c4927?w=600&q=70",
        ];
        const imgs =
          ownImgs.length >= 3
            ? ownImgs.slice(0, 3)
            : restaurant.niche === "Restaurants"
              ? restaurantFallback
              : ownImgs.slice(0, 3);
        if (!imgs.length) return null;
        return (
          <section className="max-w-6xl mx-auto px-4 py-8">
            <div className="grid grid-cols-3 gap-2 sm:gap-3 aspect-[3/1]">
              {imgs.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  className="w-full h-full object-cover rounded-lg"
                />
              ))}
            </div>
          </section>
        );
      })()}
    </div>
  );
}

function RichContent({ text }) {
  if (!text) return null;
  // Light Markdown-ish — split on blank lines, render paragraphs/headings
  const blocks = String(text)
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.startsWith("### ")) return <h3 key={i} className="text-xl font-bold mt-6 mb-1" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>{b.slice(4)}</h3>;
        if (b.startsWith("## ")) return <h2 key={i} className="text-2xl font-bold mt-8 mb-2" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>{b.slice(3)}</h2>;
        return <p key={i} className="text-[15px] leading-relaxed opacity-90">{b}</p>;
      })}
    </>
  );
}

// ============================================================
// MENU PAGE
// ============================================================
function MenuPage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  // iter373c patch H — whitelabel-aware URL base.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  const { user } = useAuth();
  const [menus, setMenus] = useState([]);
  const [activeMenu, setActiveMenu] = useState(0);
  const [holidaySection, setHolidaySection] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Themed menu-type selector (Full / Takeaway / Specials / Halloween / Christmas).
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuSel, setMenuSel] = useState(null);
  const [menuTheme, setMenuTheme] = useState(null);
  const menuInit = useRef(false);
  const dateParam = (typeof window !== "undefined")
    ? (new URLSearchParams(window.location.search).get("date") || "").trim()
    : "";
  const cart = useDemoCart(restaurant.id);
  // iter376 — Raro-style dietary filter + course jump-nav + Cards/List view.
  const [dietFilter, setDietFilter]       = useState(null);
  const [foodBevFilter, setFoodBevFilter] = useState("all");  // "all" | "food" | "beverage"
  const [viewMode, setViewMode]           = useState(() => {
    try { return localStorage.getItem("vumu:menu:view:v1") || "cards"; }
    catch { return "cards"; }
  });
  useEffect(() => {
    try { localStorage.setItem("vumu:menu:view:v1", viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  const [activeSection, setActiveSection] = useState("");

  // Owner-edit mode toggled by `?owner=1` on the URL. Investor-demo control
  // so the user can flip into edit mode mid-pitch without re-auth.
  const ownerMode = (typeof window !== "undefined") &&
    new URLSearchParams(window.location.search).get("owner") === "1";

  useEffect(() => {
    const qs = new URLSearchParams();
    if (ownerMode) qs.set("include_private", "true");
    if (menuSel) qs.set("menu", menuSel);
    if (dateParam) qs.set("date", dateParam);
    fetch(`${API}/api/directory/restaurants/${slug}/menu${qs.toString() ? `?${qs}` : ""}`)
      .then((r) => r.json())
      .then((d) => {
        setMenus(d.menus || []);
        setMenuTheme(d.menu_theme || null);
        if (d.active_template?.holiday_section) {
          setHolidaySection(d.active_template.holiday_section);
        }
      });
  }, [slug, ownerMode, reloadKey, menuSel, dateParam]);

  // Load the menu-type selector options + default (directory parity with Raro).
  useEffect(() => {
    const qs = dateParam ? `?date=${encodeURIComponent(dateParam)}` : "";
    fetch(`${API}/api/directory/restaurants/${slug}/menus/available${qs}`)
      .then((r) => r.json())
      .then((d) => {
        setMenuOptions(d.options || []);
        if (!menuInit.current) {
          menuInit.current = true;
          setMenuSel(d.default || "full");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, dateParam]);

  const handleAdd = (item) => {
    cart.add({
      id: item.id || item.name,
      name: item.name,
      price: item.price || (item.prices && (typeof item.prices === "object" ? Object.values(item.prices)[0] : item.prices[0])),
      image: item.image || item.image_url || item.photo_url,
    });
  };

  const current = menus[activeMenu];
  const sections = current?.sections || [];
  const sectionsWithHoliday = holidaySection
    ? [{ ...holidaySection, is_holiday: true }, ...sections]
    : sections;

  // iter376 — apply the diet filter across every section's items.
  // An item passes when its `dietary` array contains the selected code
  // (case-insensitive, spaces/dashes normalised).
  const _normDiet = (d) => (d || "").toString().trim().toLowerCase().replace(/\s+/g, "-");
  // Food / Beverage tab first, then dietary.
  const menuTypeFiltered = foodBevFilter === "all"
    ? sectionsWithHoliday
    : sectionsWithHoliday.filter((s) => (s.kind || s.menu_type || "food") === foodBevFilter);
  const filteredSections = dietFilter
    ? menuTypeFiltered
        .map((s) => ({
          ...s,
          items: (s.items || []).filter((it) =>
            (Array.isArray(it.dietary) ? it.dietary : [])
              .map(_normDiet).includes(_normDiet(dietFilter))
          ),
        }))
        .filter((s) => (s.items || []).length > 0)
    : menuTypeFiltered;

  // Whether the tenant actually has any beverages configured — governs
  // whether the Food/Beverage row shows.
  const hasBeverages = sectionsWithHoliday.some((s) => (s.kind || s.menu_type) === "beverage");

  // Stable anchor slug from the section name (Starters → s-starters).
  const secSlug = (name) => `s-${(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

  // Scroll-spy: highlight the course pill of whichever section sits in
  // the reading band. Re-runs whenever the filter changes (sections are re-mounted).
  useEffect(() => {
    if (!filteredSections.length) return;
    const nodes = filteredSections
      .map((s) => document.getElementById(secSlug(s.name)))
      .filter(Boolean);
    if (!nodes.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin: "-15% 0px -60% 0px", threshold: 0 },
    );
    nodes.forEach((n) => io.observe(n));
    if (!activeSection && nodes[0]) setActiveSection(nodes[0].id);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSections.map((s) => s.name).join("|")]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="menu-page">
      {/* Auto-prompt logged-in customers if they have a saved usual at this venue */}
      <UsualOrderPrompt user={user} venueSlug={slug} />

      {/* Quick Order Bar for coffee/pizza/kebab — sits at the top */}
      {isQuickOrder(restaurant) && <QuickOrderBar restaurant={restaurant} cart={cart} userId={user?.id} />}

      <div className="mb-5">
        {ownerMode && (
          <div
            data-testid="owner-mode-banner"
            className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[12px]"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className="font-bold uppercase tracking-wider text-[11px]">Owner mode</span>
            <span className="opacity-80">— tap any dish to edit name, price, ingredients, or hide from customers.</span>
          </div>
        )}
        <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold">{restaurant.menu_label || "Menu"}</p>
        <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
          {restaurant.menu_label === "Products" ? "Our services" : "What's good today"}
        </h1>
        <p className="text-[14px] opacity-70 mt-1">
          {restaurant.menu_label === "Products"
            ? "Tap any service to see full details, then book via the appointment button."
            : "Tap any dish to add it to your order. Adjust quantities in your cart."}
        </p>
      </div>

      {/* Themed menu-type selector + festive banner (Full / Takeaway /
          Specials / Halloween / Christmas) — parity with Raro. */}
      {menuTheme && (menuTheme.banner || menuTheme.menu_name) && (
        <div
          data-testid="vumu-menu-theme-banner"
          className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl text-white"
          style={{ background: menuTheme.colour || "#a01e28" }}
        >
          <span style={{ fontSize: 22 }}>{menuTheme.icon || "⭐"}</span>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-extrabold tracking-wide">
              {menuTheme.banner || menuTheme.menu_name}
            </div>
            {(menuTheme.active_from) && (
              <div className="text-[11px] opacity-90 font-medium mt-0.5">
                {menuTheme.active_from}
                {menuTheme.active_to && menuTheme.active_to !== menuTheme.active_from
                  ? ` → ${menuTheme.active_to}` : ""}
              </div>
            )}
          </div>
        </div>
      )}

      {menuOptions.length > 1 && (
        <div
          className="flex items-center gap-1.5 flex-wrap py-2 mb-2"
          data-testid="vumu-menu-type-selector"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 mr-1">Menu</span>
          {menuOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setMenuSel(opt.key)}
              data-testid={`vumu-menu-type-${opt.key}`}
              aria-pressed={menuSel === opt.key}
              className="px-3 py-1 rounded-full text-[12px] font-bold border transition-colors"
              style={menuSel === opt.key
                ? { background: "var(--tpl-accent, #292524)", color: "#fff", borderColor: "var(--tpl-accent, #292524)" }
                : { background: "#fff", borderColor: "var(--tpl-line, #e7e5e4)", color: "var(--tpl-ink, #1a1614)" }}
            >
              {opt.icon ? `${opt.icon} ` : ""}{opt.label}
              {opt.auto && opt.type === "holiday" ? " · today" : ""}
            </button>
          ))}
        </div>
      )}

      {menus.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {menus.map((m, i) => (
            <button
              key={m.id || i}
              onClick={() => setActiveMenu(i)}
              data-testid={`menu-tab-${i}`}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-[13px] font-semibold border ${
                activeMenu === i
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white border-stone-200 hover:border-stone-400"
              }`}
            >
              {m.menu_name}
            </button>
          ))}
        </div>
      )}

      {/* iter376 · Food / Beverage top-level filter (only when both exist) */}
      {hasBeverages && (
        <div
          className="flex items-center gap-1.5 flex-wrap py-2 mb-2"
          data-testid="menu-type-filter"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 mr-1">Menu</span>
          {[
            ["all",      "All"],
            ["food",     "Food"],
            ["beverage", "Beverage"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFoodBevFilter(k)}
              data-testid={`menu-type-${k}`}
              aria-pressed={foodBevFilter === k}
              className="px-3 py-1 rounded-full text-[12px] font-bold uppercase tracking-wide border transition-colors"
              style={foodBevFilter === k
                ? { background: "var(--tpl-accent, #292524)", color: "#fff", borderColor: "var(--tpl-accent, #292524)" }
                : { background: "#fff", borderColor: "var(--tpl-line, #e7e5e4)", color: "var(--tpl-ink, #1a1614)" }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* iter376 · Dietary filter chips (Raro parity) */}
      {sectionsWithHoliday.length > 0 && (
        <div
          className="flex items-center gap-1.5 flex-wrap py-2 mb-2 border-y"
          style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
          data-testid="menu-diet-filter"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60 mr-1">Filter</span>
          {[
            ["vegetarian",  "V \u00b7 Vegetarian"],
            ["vegan",       "VG \u00b7 Vegan"],
            ["gluten-free", "GF \u00b7 Gluten-free"],
            ["dairy-free",  "DF \u00b7 Dairy-free"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setDietFilter((c) => (c === k ? null : k))}
              data-testid={`menu-diet-${k}`}
              aria-pressed={dietFilter === k}
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors"
              style={dietFilter === k
                ? { background: "var(--tpl-accent, #292524)", color: "#fff", borderColor: "var(--tpl-accent, #292524)" }
                : { background: "#fff", borderColor: "var(--tpl-line, #e7e5e4)", color: "var(--tpl-ink, #1a1614)" }}
            >
              {label}
            </button>
          ))}
          {dietFilter && (
            <button
              type="button"
              onClick={() => setDietFilter(null)}
              data-testid="menu-diet-clear"
              className="text-[10px] font-bold uppercase tracking-[0.15em] text-red-700 hover:underline ml-1"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* iter376 · Course jump-nav + Cards/List view toggle */}
      {filteredSections.length > 0 && (
        <nav
          className="sticky top-14 md:top-16 z-20 bg-white/95 backdrop-blur-xl border-y py-2 mb-3 flex items-center gap-1.5 flex-wrap"
          style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
          data-testid="menu-course-nav"
        >
          {filteredSections.map((s) => {
            const anchor = secSlug(s.name);
            const on = activeSection === anchor;
            return (
              <a
                key={anchor}
                href={`#${anchor}`}
                data-testid={`menu-course-${anchor}`}
                aria-current={on ? "location" : undefined}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold border whitespace-nowrap"
                style={on
                  ? { background: "var(--tpl-accent, #292524)", color: "#fff", borderColor: "var(--tpl-accent, #292524)" }
                  : { background: "#fff", borderColor: "var(--tpl-line, #e7e5e4)", color: "var(--tpl-ink, #1a1614)" }}
              >
                {s.name}
                <span className="ml-1 opacity-70">({(s.items || []).length})</span>
              </a>
            );
          })}
          <div
            className="ml-auto inline-flex gap-0.5 bg-white rounded-full border p-0.5"
            style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
            data-testid="menu-view-toggle"
          >
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              data-testid="menu-view-cards"
              aria-pressed={viewMode === "cards"}
              className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
              style={viewMode === "cards"
                ? { background: "var(--tpl-accent, #292524)", color: "#fff" }
                : { color: "var(--tpl-ink, #1a1614)" }}
            >
              <LayoutGrid className="w-3 h-3" /> Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              data-testid="menu-view-list"
              aria-pressed={viewMode === "list"}
              className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
              style={viewMode === "list"
                ? { background: "var(--tpl-accent, #292524)", color: "#fff" }
                : { color: "var(--tpl-ink, #1a1614)" }}
            >
              <ListIcon className="w-3 h-3" /> List
            </button>
          </div>
        </nav>
      )}

      <div className="space-y-4">
        {sectionsWithHoliday.length === 0 && restaurant.enrichment_status && restaurant.enrichment_status !== "ready" && (
          <div
            className="rounded-2xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-white p-8 text-center"
            data-testid="menu-curating-state"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wider mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              AI Curating
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
              {restaurant.menu_label === "Products" ? "Your services menu" : "Your menu"} is being built
            </h2>
            <p className="text-[14px] text-stone-600 max-w-md mx-auto">
              Our AI is reading your existing website, mirroring your service catalog,
              writing SEO-rich descriptions, and matching imagery. Ready to publish
              within <span className="font-bold">{restaurant.enrichment_eta_days || 7} days</span>.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-[12px] text-stone-500">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Scraping site</span>
              <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${["wp_mirrored","image_generation","copy_review","ready"].includes(restaurant.enrichment_status) ? "bg-emerald-500" : "bg-stone-300"}`} /> Mirroring WP</span>
              <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${["image_generation","copy_review","ready"].includes(restaurant.enrichment_status) ? "bg-emerald-500" : "bg-stone-300"}`} /> Image generation</span>
              <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${["copy_review","ready"].includes(restaurant.enrichment_status) ? "bg-emerald-500" : "bg-stone-300"}`} /> Copy review</span>
            </div>
            {restaurant.claim_source_url && (
              <a
                href={restaurant.claim_source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-900 underline"
                data-testid="menu-source-url"
              >
                Mirroring from {(() => { try { return new URL(restaurant.claim_source_url).hostname; } catch { return ""; } })()}
              </a>
            )}
          </div>
        )}
        {filteredSections.map((s, si) => (
          <div
            key={si + (s.name || "")}
            id={secSlug(s.name)}
            className="rounded-xl p-4 border bg-white/95 scroll-mt-32"
            style={s.is_holiday ? {
              borderColor: "var(--tpl-accent)",
              borderWidth: 2,
            } : { borderColor: "rgba(120,113,108,0.18)" }}
            data-testid={s.is_holiday ? "holiday-section" : `section-${si}`}
          >
            <h2
              className="text-lg font-bold mb-2 pb-1 border-b flex items-center gap-2 text-stone-900"
              style={{ borderColor: "var(--tpl-line, #e7e5e4)", fontFamily: "var(--tpl-font-display, serif)" }}
            >
              {s.is_holiday && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: "var(--tpl-accent)", color: "var(--tpl-accent-ink)" }}
                >
                  Limited
                </span>
              )}
              {s.name}
            </h2>
            {s.footnote && <p className="text-[12px] italic text-stone-700 mb-2">{s.footnote}</p>}
            <ul
              className={viewMode === "cards"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                : "divide-y"}
              style={viewMode === "cards" ? undefined : { borderColor: "var(--tpl-line, #e7e5e4)" }}
            >
              {(s.items || []).map((it, ii) => {
                const sizes = Array.isArray(it.prices)
                  ? it.prices.filter((p) => p !== "" && p !== null && p !== undefined)
                  : (it.prices && typeof it.prices === "object" ? Object.values(it.prices).filter(Boolean) : []);
                // Positional size labels (Coffee Commune-style: S/M/L; wines: Glass/Bottle)
                const sizeLabelGuess = (n) => {
                  if (n === 2) return ["Glass", "Bottle"];
                  if (n === 3) return ["Small", "Medium", "Large"];
                  if (n === 4) return ["S", "M", "L", "XL"];
                  return Array(n).fill(0).map((_, i) => `Option ${i + 1}`);
                };
                const sizeLabels = Array.isArray(it.size_labels) && it.size_labels.length === sizes.length
                  ? it.size_labels
                  : sizeLabelGuess(sizes.length);
                const singlePrice = it.price && (!sizes.length || sizes.length === 0);
                const itemBase = { ...it, id: it.id || `${si}-${ii}-${it.name}` };
                return (
                  <li
                    key={ii}
                    className={viewMode === "cards"
                      ? "flex flex-col gap-2 p-3 rounded-xl border bg-white cursor-pointer hover:shadow-md transition-shadow"
                      : "flex items-start gap-3 py-2.5 cursor-pointer hover:bg-white -mx-2 px-2 rounded-md transition-colors"}
                    style={viewMode === "cards" ? { borderColor: "var(--tpl-line, #e7e5e4)" } : undefined}
                    data-testid={`menu-item-${si}-${ii}`}
                    onClick={() => ownerMode ? setEditItem(itemBase) : setDetailItem(itemBase)}
                  >
                    {(it.image || it.image_url || it.photo_url) && (
                      <img
                        src={it.image || it.image_url || it.photo_url}
                        alt=""
                        className={viewMode === "cards"
                          ? "w-full h-40 rounded-lg object-cover bg-stone-200"
                          : "w-16 h-16 rounded-md object-cover bg-stone-200 shrink-0"}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[14px] capitalize text-stone-900 flex items-center gap-1.5">
                        {it.name}
                        {ownerMode && it.is_public === false && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                            Hidden
                          </span>
                        )}
                        {ownerMode && it.is_internal === true && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-stone-200 text-stone-700">
                            Staff only
                          </span>
                        )}
                      </div>
                      {it.description && (
                        <div className="text-[12px] text-stone-600 leading-snug mt-0.5">{it.description}</div>
                      )}
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {(Array.isArray(it.dietary) ? it.dietary : []).slice(0, 3).map((d) => (
                          <span key={d} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{d}</span>
                        ))}
                        {Array.isArray(it.wine_pairing_ids) && it.wine_pairing_ids.length > 0 && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">🍷 Paired</span>
                        )}
                        <span className="text-[10px] text-stone-500 italic ml-1">Tap for details</span>
                      </div>
                      {/* Multi-size selector — each size adds a separate cart line.
                          Customer can add Small + Large in two clicks without leaving the item. */}
                      {sizes.length > 1 && (
                        <div
                          className="flex items-center gap-1.5 mt-2 flex-wrap"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`size-chips-${si}-${ii}`}
                        >
                          {sizes.map((p, si2) => (
                            <button
                              key={si2}
                              data-testid={`size-chip-${si}-${ii}-${si2}`}
                              onClick={() => handleAdd({
                                ...itemBase,
                                id: `${itemBase.id}__${sizeLabels[si2]}`,
                                name: `${itemBase.name} (${sizeLabels[si2]})`,
                                price: Number(p),
                                prices: undefined,
                                size_chosen: sizeLabels[si2],
                              })}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-stone-300 hover:border-stone-700 hover:bg-stone-50 text-[11px] font-semibold text-stone-900 transition-colors"
                            >
                              <span>{sizeLabels[si2]}</span>
                              <span className="tabular-nums text-stone-700">${p}</span>
                              <span className="ml-0.5 w-4 h-4 rounded-full text-white text-[10px] grid place-items-center" style={{ background: "var(--tpl-accent, #292524)" }}>+</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={viewMode === "cards"
                        ? "mt-auto pt-2 flex items-center justify-between gap-2 border-t border-stone-100"
                        : "shrink-0 flex flex-col items-end gap-1"}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Always show a price for single-price items; show a
                          range like $16-$74 when the item has multi-sizes. */}
                      {sizes.length <= 1 && Number(it.price) > 0 && (
                        <div className="font-bold text-[15px] tabular-nums text-stone-900" data-testid={`menu-price-${si}-${ii}`}>
                          ${Number(it.price).toFixed(Number(it.price) % 1 === 0 ? 0 : 2)}
                        </div>
                      )}
                      {sizes.length > 1 && (
                        <div className="font-bold text-[13px] tabular-nums text-stone-900" data-testid={`menu-price-range-${si}-${ii}`}>
                          ${Math.min(...sizes.map(Number))} <span className="opacity-60">–</span> ${Math.max(...sizes.map(Number))}
                        </div>
                      )}
                      {ownerMode ? (
                        <button
                          onClick={() => setEditItem(itemBase)}
                          data-testid={`owner-edit-btn-${si}-${ii}`}
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
                        >
                          <Pencil className="w-3 h-3" strokeWidth={2.5} />
                          Edit
                        </button>
                      ) : sizes.length <= 1 && (
                        <button
                          onClick={() => handleAdd(itemBase)}
                          data-testid={`add-to-cart-${si}-${ii}`}
                          className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded text-white hover:opacity-80 shadow-sm"
                          style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {sectionsWithHoliday.length === 0 && (
          <div className="text-center py-12 opacity-60">No menu items yet.</div>
        )}
        {sectionsWithHoliday.length > 0 && filteredSections.length === 0 && (
          <div className="text-center py-12 opacity-60" data-testid="menu-diet-empty">
            No dishes match the <b>{dietFilter}</b> filter.
            <button onClick={() => setDietFilter(null)} className="ml-1 underline">Clear filter</button>
          </div>
        )}
      </div>

      {/* Item detail modal — wine pairings, allergens, calories, ingredients */}
      <MenuItemDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onAddPrimary={(it) => { handleAdd(it); setDetailItem(null); }}
      />

      {/* Owner-edit modal — only mounts when ?owner=1 and an item is selected */}
      {ownerMode && (
        <OwnerEditMenuItemModal
          slug={slug}
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

// ============================================================
// ABOUT PAGE
// ============================================================
function AboutPage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  // iter373c patch H — whitelabel-aware URL base.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  const [reviews, setReviews] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ rating: 5, author_name: "", title: "", body: "", author_email: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = () => {
    fetch(`${API}/api/directory/restaurants/${slug}/reviews`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setReviews(d && d.summary ? d : null))
      .catch(() => setReviews(null));
  };
  useEffect(loadReviews, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${slug}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!r.ok) throw new Error(await r.text());
      setDraft({ rating: 5, author_name: "", title: "", body: "", author_email: "" });
      setShowForm(false);
      loadReviews();
    } catch (err) {
      alert(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="about-page">
      <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-1">About</p>
      <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
        Our story
      </h1>
      <div className="space-y-4">
        {restaurant.aeo_content_html ? (
          <div
            data-testid="about-page-body-html"
            className="hp-rich"
            style={{ lineHeight: 1.75 }}
            dangerouslySetInnerHTML={{ __html: restaurant.aeo_content_html }}
          />
        ) : (
          <RichContent text={restaurant.aeo_content || restaurant.ai_site_description || restaurant.business_description || restaurant.ai_summary} />
        )}
        {restaurant.ai_strengths && Array.isArray(restaurant.ai_strengths) && !restaurant.aeo_content_html && (
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>What we're known for</h2>
            <ul className="space-y-1.5">
              {restaurant.ai_strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2"><span style={{color: "var(--tpl-accent)"}}>•</span> {s}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Reviews */}
      {reviews?.summary && (
        <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--tpl-line)" }} data-testid="reviews-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>Reviews</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="flex">
                  {[1,2,3,4,5].map(i => (
                    <Star
                      key={i}
                      className="w-4 h-4"
                      fill={i <= Math.round(reviews.summary.average || 0) ? "currentColor" : "none"}
                      style={{ color: "var(--tpl-accent, #d97706)" }}
                    />
                  ))}
                </div>
                <span className="font-bold">{reviews.summary.average || "—"}</span>
                <span className="opacity-60 text-[13px]">· {reviews.summary.count || 0} review{(reviews.summary.count || 0) !== 1 ? "s" : ""}</span>
              </div>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-3 py-1.5 rounded-full text-[12px] font-bold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
              data-testid="write-review-btn"
            >
              {showForm ? "Cancel" : "Write a review"}
            </button>
          </div>

          {showForm && (
            <form onSubmit={submit} className="rounded-xl p-4 border mb-5 space-y-2.5"
                  style={{ borderColor: "var(--tpl-line)" }} data-testid="review-form">
              <div>
                <label className="block text-[11px] font-bold opacity-70 mb-1">Your rating</label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(i => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setDraft({ ...draft, rating: i })}
                      data-testid={`star-${i}`}
                    >
                      <Star className="w-7 h-7" fill={i <= draft.rating ? "currentColor" : "none"}
                            style={{ color: "var(--tpl-accent, #d97706)" }} />
                    </button>
                  ))}
                </div>
              </div>
              <input
                placeholder="Your name"
                value={draft.author_name}
                onChange={(e) => setDraft({ ...draft, author_name: e.target.value })}
                className="w-full px-3 py-2 rounded-md border text-[14px]"
                style={{ borderColor: "var(--tpl-line)" }}
                required
                data-testid="review-name"
              />
              <input
                type="email"
                placeholder="Email (kept private)"
                value={draft.author_email}
                onChange={(e) => setDraft({ ...draft, author_email: e.target.value })}
                className="w-full px-3 py-2 rounded-md border text-[14px]"
                style={{ borderColor: "var(--tpl-line)" }}
                data-testid="review-email"
              />
              <input
                placeholder="Title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full px-3 py-2 rounded-md border text-[14px]"
                style={{ borderColor: "var(--tpl-line)" }}
                data-testid="review-title"
              />
              <textarea
                placeholder="Tell us about your visit…"
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                className="w-full px-3 py-2 rounded-md border text-[14px] h-24"
                style={{ borderColor: "var(--tpl-line)" }}
                required
                data-testid="review-body"
              />
              <button
                disabled={submitting}
                className="w-full px-3 py-2 rounded-md text-[14px] font-bold text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
                data-testid="review-submit"
              >
                {submitting ? "Posting…" : "Post review"}
              </button>
            </form>
          )}

          <ul className="space-y-3">
            {reviews.reviews.slice(0, 10).map((r) => (
              <li key={r.review_id} className="rounded-xl p-3 border" style={{ borderColor: "var(--tpl-line)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-3.5 h-3.5"
                            fill={i <= r.rating ? "currentColor" : "none"}
                            style={{ color: "var(--tpl-accent, #d97706)" }} />
                    ))}
                  </div>
                  <span className="font-bold text-[13px]">{r.author_name}</span>
                  <span className="text-[11px] opacity-50">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-AU") : ""}
                  </span>
                </div>
                {r.title && <div className="font-semibold text-[14px]">{r.title}</div>}
                {r.body && <p className="text-[13px] opacity-85 mt-0.5">{r.body}</p>}
              </li>
            ))}
            {reviews.reviews.length === 0 && (
              <li className="text-center py-6 opacity-60 italic">No reviews yet — be the first.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================
// FACILITY ROW — one line in the closest-facilities list.
// Wraps in a Popover that opens on hover (desktop) OR tap (mobile).
// Popover auto-sizes and exposes:
//   • Address  → opens Google Maps (uses stored address, else falls back
//                to "{name}, {ownerSuburb}" as the search query)
//   • Phone    → tel: link (tap-to-call). Skipped when we don't have one.
// ============================================================
function FacilityRow({ facility, mode, index, ownerSuburb }) {
  const [open, setOpen] = useState(false);
  const f = facility || {};

  // Build a Google Maps URL. Prefer explicit lat/lng when the POI has
  // them, otherwise fall back to a free-text search (name + suburb).
  const mapsUrl = f.lat && f.lng
    ? `https://www.google.com/maps?q=${f.lat},${f.lng}`
    : `https://www.google.com/maps?q=${encodeURIComponent(
        [f.name, ownerSuburb, "AU"].filter(Boolean).join(", ")
      )}`;

  // Sanitise phone into a tel:-safe form (+, digits only)
  const telHref = f.phone
    ? `tel:${String(f.phone).replace(/[^\d+]/g, "")}`
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <li
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          tabIndex={0}
          className="flex items-center justify-between gap-2 cursor-pointer rounded px-1.5 py-1 -mx-1.5 hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ "--tw-ring-color": "var(--tpl-accent, #292524)" }}
          data-testid={`facility-row-${mode}-${index}`}
        >
          <span className="truncate">
            {mode === "bus" && f.stop_no && (
              <span className="inline-block mr-1 px-1.5 py-0 rounded text-[10px] font-bold tabular-nums"
                    style={{ backgroundColor: "var(--tpl-accent, #292524)", color: "#fff" }}
                    data-testid={`bus-stop-no-${index}`}>
                #{f.stop_no}
              </span>
            )}
            {f.name}
            {(mode === "bus" || mode === "train" || mode === "ferry") && f.routes && (
              <span className="ml-1 text-[11px] opacity-70">· {f.routes}</span>
            )}
          </span>
          <span className="text-[11px] opacity-70 shrink-0 tabular-nums">
            {f.walking_time_min}min walk · {f.distance_m}m
          </span>
        </li>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={8}
        className="w-auto max-w-xs p-3 space-y-1.5"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        data-testid={`facility-popover-${mode}-${index}`}
      >
        <div className="font-bold text-[13px] leading-tight" data-testid="facility-popover-name">
          {f.name}
        </div>
        {(mode === "bus" || mode === "train" || mode === "ferry") && f.routes && (
          <div className="text-[11px] opacity-70">Routes: {f.routes}</div>
        )}
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-start gap-1.5 text-[12px] text-blue-700 hover:underline break-words"
          data-testid="facility-popover-address"
        >
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{f.address || `${f.name}${ownerSuburb ? `, ${ownerSuburb}` : ""} — open in Google Maps`}</span>
        </a>
        {telHref ? (
          <a
            href={telHref}
            className="flex items-center gap-1.5 text-[12px] text-emerald-700 hover:underline"
            data-testid="facility-popover-phone"
          >
            <Phone className="w-3.5 h-3.5 shrink-0" />
            <span className="tabular-nums">{f.phone}</span>
          </a>
        ) : (
          // No phone in our DB → send the user to Google Maps where the
          // official listing (and phone number) is one tap away.
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-[12px] text-emerald-700 hover:underline"
            data-testid="facility-popover-findphone"
          >
            <Phone className="w-3.5 h-3.5 shrink-0" />
            <span>Find phone on Google Maps →</span>
          </a>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ============================================================
// CONTACT PAGE
// ============================================================
function AppointmentPage() {
  const {restaurant} = useSite();
  const url = restaurant.external_booking_url;
  let safe = false;
  try { safe = new URL(url).protocol === "https:"; } catch (_) {}
  if (!safe) return <section className="p-6"><h1>Online booking is unavailable</h1><p>Please use the contact page to enquire.</p></section>;
  return <section className="p-4 sm:p-6"><h1 className="text-3xl font-bold mb-3">Book an Appointment</h1>
    <p className="mb-4">{restaurant.name}</p>
    <a href={url} target="_blank" rel="noopener noreferrer" className="underline block mb-5">Open booking form in a new tab</a>
    <PartnerPanel url={url} label={restaurant.booking_provider === "timely" ? "Timely" : "online booking"} loading={false} fullHeight />
  </section>;
}

function ContactPage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  // iter373c patch H — whitelabel-aware URL base.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [activeFacility, setActiveFacility] = useState("bus");
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formSent, setFormSent] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/directory/restaurants/${slug}/contact-info`)
      .then((r) => r.json())
      .then(setInfo);
  }, [slug]);

  // Reuse the same origin-checked resize protocol as SSR and HC embeds.
  useEffect(()=>{
    if(!info?.hustleshield_form_url || document.querySelector('script[data-hsf-resize]'))return;
    const script=document.createElement("script");script.src=API+"/api/forms/embed-resize.js";
    script.dataset.hsfResize="true";script.async=true;document.head.appendChild(script);
  },[info?.hustleshield_form_url]);

  // iter361: scroll the enquiry form into view when navigated to via
  // `…/contact#enquiry` (from the "Send an enquiry" CTAs in the header
  // and hero). We wait for `info` to resolve so the form has mounted.
  useEffect(() => {
    if (!info) return;
    if (location.hash === "#enquiry") {
      const el = document.getElementById("enquiry");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [info, location.hash]);

  if (!info) return <div className="p-12 text-center opacity-60">Loading…</div>;

  const facilities = info.facilities || {};
  // Bottleshops tab is only relevant for BYO or Licensed+BYO venues
  const showBottleshops = (info.byo || info.license_status === "byo" || info.license_status === "licensed_and_byo")
    && (facilities.bottleshops?.length || 0) > 0;
  const tabs = [
    { key: "bus", label: "Bus", icon: Bus, count: facilities.bus?.length || 0 },
    { key: "train", label: "Trains", icon: Train, count: facilities.train?.length || 0 },
    { key: "ferry", label: "Ferries", icon: Ship, count: facilities.ferry?.length || 0 },
    { key: "toilets", label: "Toilets", icon: Toilet, count: facilities.toilets?.length || 0 },
    { key: "parks", label: "Parks", icon: Trees, count: facilities.parks?.length || 0 },
    ...(showBottleshops
      ? [{ key: "bottleshops", label: "Bottleshops", icon: Wine, count: facilities.bottleshops?.length || 0 }]
      : []),
    { key: "licensed_byo", label: "BYO / Licensed", icon: Wine, count: facilities.licensed_byo?.length || 0 },
  ].filter(t => t.count > 0);

  const activeList = facilities[activeFacility] || [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="contact-page">
      <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-1">Contact</p>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
        Find us, call us, drop by
      </h1>

      <div
        className={`grid gap-6 ${
          (info.enable_facilities || info.enable_trip_planner)
            ? "md:grid-cols-[1fr_1fr]"
            : "md:grid-cols-1"
        }`}
        data-testid="contact-grid"
      >
        <div className="space-y-5">
          {/* Address + phone + email */}
          <div className="rounded-xl p-4 space-y-2.5 border" style={{ borderColor: "var(--tpl-line)" }}>
            {info.address && (
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tpl-accent)" }} />
                <div>
                  <div className="font-semibold text-[14px]">{info.address}</div>
                  <div className="text-[12px] opacity-65">{info.suburb} {info.postcode}</div>
                </div>
              </div>
            )}
            {info.phone && (
              <a href={`tel:${info.phone}`} className="flex items-center gap-2.5 hover:underline" data-testid="contact-phone">
                <Phone className="w-4 h-4 shrink-0" style={{ color: "var(--tpl-accent)" }} />
                <span className="font-semibold text-[14px]">{info.phone}</span>
              </a>
            )}
            {info.mobile && (
              <a href={`tel:${info.mobile}`} className="flex items-center gap-2.5 hover:underline" data-testid="contact-mobile">
                <Phone className="w-4 h-4 shrink-0" style={{ color: "var(--tpl-accent)" }} />
                <span className="font-semibold text-[14px]">{info.mobile}</span>
                <span className="text-[10px] opacity-60 uppercase tracking-wider">Mobile</span>
              </a>
            )}
            {info.email && (
              <a href={`mailto:${info.email}`} className="flex items-center gap-2.5 hover:underline">
                <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--tpl-accent)" }} />
                <span className="font-semibold text-[14px]">{info.email}</span>
              </a>
            )}
            {info.website && (
              <a href={info.website} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 hover:underline">
                <Globe className="w-4 h-4 shrink-0" style={{ color: "var(--tpl-accent)" }} />
                <span className="font-semibold text-[14px]">{info.website}</span>
              </a>
            )}
            <div className="flex gap-2 pt-1">
              {info.facebook && <a href={info.facebook} target="_blank" rel="noreferrer"><Facebook className="w-4 h-4" /></a>}
              {info.instagram && <a href={info.instagram} target="_blank" rel="noreferrer"><Instagram className="w-4 h-4" /></a>}
            </div>
          </div>

          {/* Hours */}
          {info.hours && info.hours.length > 0 && (
            <div className="rounded-xl p-4 border" style={{ borderColor: "var(--tpl-line)" }}>
              <h3 className="font-bold text-[15px] mb-2 flex items-center gap-1.5">
                <Clock className="w-4 h-4" style={{ color: "var(--tpl-accent)" }} /> Opening hours
              </h3>
              <ul className="text-[13px] space-y-0.5">
                {info.hours.map((d) => (
                  <li key={d.day} className="flex justify-between">
                    <span>{d.day}</span>
                    <span className="opacity-70 tabular-nums">{d.closed ? "Closed" : `${d.open} – ${d.close}`}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact form — uses vendor's contact_form_config when present */}
          <div id="enquiry" style={{ scrollMarginTop: "80px" }} data-testid="enquiry-anchor">
          {info.is_claimed ? (
            formSent ? (
              <div className="rounded-xl p-4 border bg-emerald-50 border-emerald-200 text-emerald-800 text-[14px]" data-testid="form-sent">
                {info.contact_form_config?.success_message || "Thanks — we'll be in touch shortly."}
              </div>
            ) : info.hustleshield_form_url ? (
              <section className="rounded-xl border p-2" style={{borderColor:"var(--tpl-line)"}}>
                <iframe src={info.hustleshield_form_url} data-hp-slug={info.hustleshield_form_slug}
                  title="Secure business contact form" loading="lazy" referrerPolicy="strict-origin-when-cross-origin"
                  style={{display:"block",width:"100%",height:860,border:0,background:"transparent"}}/>
                <p className="text-center text-sm py-3"><a href={"https://app.hustleshield.au/admin/forms?directory_business_id="+encodeURIComponent(info.hustleshield_business_id)} target="_blank" rel="noopener noreferrer">Business owner? Manage your form</a></p>
              </section>
            ) : info.contact_form_config?.fields?.length ? (
              <VendorContactForm
                slug={slug}
                config={info.contact_form_config}
                onSent={() => setFormSent(true)}
              />
            ) : (
              <form
                className="rounded-xl p-4 space-y-2.5 border"
                style={{ borderColor: "var(--tpl-line)" }}
                onSubmit={(e) => { e.preventDefault(); setFormSent(true); }}
                data-testid="contact-form"
              >
                <h3 className="font-bold text-[15px]">Send a message</h3>
                <input
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border text-[14px]"
                  style={{ borderColor: "var(--tpl-line)" }}
                  required
                  data-testid="form-name"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border text-[14px]"
                  style={{ borderColor: "var(--tpl-line)" }}
                  required
                  data-testid="form-email"
                />
                <textarea
                  placeholder="Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border text-[14px] h-24"
                  style={{ borderColor: "var(--tpl-line)" }}
                  required
                  data-testid="form-message"
                />
                <button
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[14px] font-bold text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
                  data-testid="form-submit"
                >
                  <Send className="w-3.5 h-3.5" /> Send
                </button>
              </form>
            )
          ) : (
            <div className="rounded-xl p-4 border bg-amber-50 border-amber-200 text-amber-900 text-[13px]" data-testid="contact-form-locked">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Contact form available once this business is claimed.{" "}
              <Link to={`/claim-business?slug=${slug}`} className="underline font-bold">Claim now</Link>
            </div>
          )}
          </div>
        </div>

        {/* Right column — Closest Facilities + Trip Planner. Both blocks
            are opt-in plugins configured per-tenant in Profile → Settings
            → Plugins. Default OFF for every existing tenant. If both
            plugins are OFF the entire right column is skipped (the left
            column takes full width via the grid template above). */}
        {(info.enable_facilities || info.enable_trip_planner) && (
        <div>
          {/* Plugin 1 — Closest Facilities */}
          {info.enable_facilities && (
          <div className="rounded-xl p-4 border" style={{ borderColor: "var(--tpl-line)" }} data-testid="closest-facilities">
            <h3 className="font-bold text-[15px] mb-3 flex items-center gap-2">
              <span>Closest facilities (within 5km)</span>
              {(info.byo || info.licensed) && (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: "var(--tpl-accent, #292524)", color: "#fff" }}
                      data-testid="license-pill">
                  {info.license_status === "licensed_and_byo" ? "Licensed + BYO"
                    : info.license_status === "byo"          ? "BYO"
                    : info.license_status === "licensed"     ? "Licensed"
                    : ""}
                </span>
              )}
            </h3>
            <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="facility-tabs">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveFacility(t.key)}
                  data-testid={`facility-tab-${t.key}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
                  style={activeFacility === t.key
                    ? { backgroundColor: "var(--tpl-accent, #292524)", borderColor: "var(--tpl-accent, #292524)", color: "#fff" }
                    : { borderColor: "var(--tpl-line)", color: "var(--tpl-ink, #1a1614)", opacity: 0.75 }
                  }
                >
                  <t.icon className="w-3 h-3" /> {t.label} <span style={{ opacity: 0.75 }}>({t.count})</span>
                </button>
              ))}
            </div>
            <ul className="space-y-1.5 text-[13px]" data-testid={`facility-list-${activeFacility}`}>
              {activeList.length === 0 ? (
                <li className="opacity-60 italic">No data within 5km.</li>
              ) : activeList.map((f, i) => (
                <FacilityRow
                  key={i}
                  index={i}
                  facility={f}
                  mode={activeFacility}
                  ownerSuburb={info.suburb || ""}
                />
              ))}
            </ul>
            {info.lat && (
              <a
                href={`https://www.google.com/maps?q=${info.lat},${info.lng}`}
                target="_blank" rel="noreferrer"
                className="block mt-3 text-[12px] underline opacity-80"
                data-testid="open-in-maps"
              >
                Open in Google Maps →
              </a>
            )}
          </div>
          )}

          {/* Plugin 2 — Trip Planner. Sits under Facilities when both
              are on; renders alone in the right column when Facilities
              is off but Trip Planner is on. */}
          {info.enable_trip_planner && (
          <div
            className={`${info.enable_facilities ? "mt-4" : ""} rounded-xl p-4 border`}
            style={{ borderColor: "var(--tpl-line)" }}
            data-testid="trip-planner"
          >
            <h3 className="font-bold text-[15px] mb-2 flex items-center gap-1.5">
              <Calendar className="w-4 h-4" style={{ color: "var(--tpl-accent)" }} />
              Trip planner
            </h3>
            <p className="text-[12px] opacity-70 mb-2">
              Got a booking? We'll work out when to leave.
            </p>
            <TripPlanner facilities={facilities} restaurant={info} />
          </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// ─── Vendor Contact Form ───────────────────────────────────────────
function VendorContactForm({ slug, config, onSent }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setV = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/api/directory/vendor/${slug}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      onSent && onSent();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <form
      className="rounded-xl p-4 space-y-2.5 border"
      style={{ borderColor: "var(--tpl-line)" }}
      onSubmit={submit}
      data-testid="contact-form"
    >
      <h3 className="font-bold text-[15px]">Send an enquiry</h3>
      {(config.fields || []).map((f) => {
        const common = {
          "data-testid": `form-${f.name}`,
          className: "w-full px-3 py-2 rounded-md border text-[14px]",
          style: { borderColor: "var(--tpl-line)" },
          required: !!f.required,
          value: values[f.name] || (f.type === "checkbox" ? false : ""),
          onChange: (e) => setV(f.name, f.type === "checkbox" ? e.target.checked : e.target.value),
        };
        if (f.type === "textarea") {
          return (
            <label key={f.name} className="block text-[12px] font-semibold text-stone-600">
              {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
              <textarea {...common} rows={f.rows || 4} placeholder={f.hint || ""} className={common.className + " h-24 mt-1 font-normal"} />
            </label>
          );
        }
        if (f.type === "select") {
          return (
            <label key={f.name} className="block text-[12px] font-semibold text-stone-600">
              {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
              <select {...common} className={common.className + " mt-1 font-normal"}>
                <option value="">Select…</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
          );
        }
        if (f.type === "checkbox") {
          return (
            <label key={f.name} className="flex items-start gap-2 text-[12px] text-stone-600" data-testid={`form-${f.name}-wrap`}>
              <input type="checkbox" checked={!!values[f.name]} onChange={(e) => setV(f.name, e.target.checked)} className="mt-0.5" data-testid={`form-${f.name}`} />
              <span>{f.label}</span>
            </label>
          );
        }
        return (
          <label key={f.name} className="block text-[12px] font-semibold text-stone-600">
            {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
            <input
              type={f.type || "text"}
              {...common}
              placeholder={f.hint || ""}
              maxLength={f.maxlength || undefined}
              pattern={f.pattern || undefined}
              className={common.className + " mt-1 font-normal"}
            />
          </label>
        );
      })}
      {config.consent_note && (
        <p className="text-[11px] text-stone-500 leading-relaxed">{config.consent_note}</p>
      )}
      {err && <div className="text-[12px] text-rose-600" data-testid="form-error">{err}</div>}
      <button
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[14px] font-bold text-white hover:opacity-90 disabled:opacity-60"
        style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
        data-testid="form-submit"
      >
        <Send className="w-3.5 h-3.5" /> {config.submit_label || "Submit"}
      </button>
    </form>
  );
}

function TripPlanner({ facilities, restaurant }) {
  const [bookingTime, setBookingTime] = useState("19:00");
  const [bookingDate, setBookingDate] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  });
  const [travelMode, setTravelMode] = useState("transit");
  const [transitFilter, setTransitFilter] = useState("all");
  const [leaveState, setLeaveState] = useState("QLD");
  const [suburbQuery, setSuburbQuery] = useState("");
  const [suburbPicked, setSuburbPicked] = useState(null);
  const [suburbHits, setSuburbHits] = useState([]);
  const [street, setStreet] = useState("");
  const [suburbOpen, setSuburbOpen] = useState(false);
  const [journey, setJourney] = useState(null);   // {mode,total_min,legs,origin_stop,dest_stop,...}
  const [weather, setWeather] = useState(null);

  const AU_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"];

  const availableTransit = [
    { key: "all",   label: "All",    icon: null,  count: (facilities.bus?.length || 0) + (facilities.train?.length || 0) + (facilities.ferry?.length || 0) },
    { key: "bus",   label: "Bus",    icon: Bus,   count: facilities.bus?.length || 0 },
    { key: "train", label: "Train",  icon: Train, count: facilities.train?.length || 0 },
    { key: "ferry", label: "Ferry",  icon: Ship,  count: facilities.ferry?.length || 0 },
  ].filter((t) => t.key === "all" || t.count > 0);

  // Fuzzy suburb search
  useEffect(() => {
    if (!suburbQuery || suburbQuery.length < 2) { setSuburbHits([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/wp/au/suburbs?state=${encodeURIComponent(leaveState)}&q=${encodeURIComponent(suburbQuery)}&limit=8`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const items = (j.items || j.suburbs || []).map((x) => ({
          suburb_name: x.suburb_name || x.suburb || "",
          state_code:  x.state_code  || x.state  || leaveState,
          postcode:    x.postcode    || "",
          latitude:    x.latitude,
          longitude:   x.longitude,
        })).filter((x) => x.suburb_name);
        if (!cancelled) setSuburbHits(items);
      } catch { if (!cancelled) setSuburbHits([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [suburbQuery, leaveState]);

  // Recompute journey whenever origin/mode/time/date changes
  useEffect(() => {
    if (!suburbPicked || !restaurant?.lat || !restaurant?.lng
        || !suburbPicked.latitude || !suburbPicked.longitude) {
      setJourney(null); return;
    }
    let cancelled = false;
    const q = new URLSearchParams({
      origin_lat: String(suburbPicked.latitude),
      origin_lng: String(suburbPicked.longitude),
      dest_lat:   String(restaurant.lat),
      dest_lng:   String(restaurant.lng),
      arrive_time: bookingTime,
      mode:        travelMode,
      transit_filter: transitFilter,
    });
    fetch(`${API}/api/directory/journey?${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j) setJourney(j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [suburbPicked, travelMode, transitFilter, bookingTime, restaurant?.lat, restaurant?.lng]);

  // Weather for booking date + time at venue location
  useEffect(() => {
    if (!restaurant?.lat || !restaurant?.lng) { setWeather(null); return; }
    let cancelled = false;
    const q = new URLSearchParams({
      lat:  String(restaurant.lat),
      lng:  String(restaurant.lng),
      date: bookingDate,
      time: bookingTime,
    });
    fetch(`${API}/api/directory/weather?${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j) setWeather(j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [restaurant?.lat, restaurant?.lng, bookingDate, bookingTime]);

  const [h, m] = bookingTime.split(":").map(Number);
  const isPeak = (h >= 7 && h < 9) || (h >= 16 && h < 19);
  const totalMin = journey?.total_min || null;

  const leaveStr = totalMin != null ? (() => {
    const leaveTotal = h * 60 + m - totalMin;
    const leaveH = Math.floor((leaveTotal + 1440) % 1440 / 60);
    const leaveM = (leaveTotal + 1440) % 60;
    return `${String(leaveH).padStart(2, "0")}:${String(leaveM).padStart(2, "0")}`;
  })() : null;

  if (!restaurant?.lat) return null;

  const modeBtn = (key, label, Icon) => (
    <button
      key={key}
      onClick={() => setTravelMode(key)}
      data-testid={`trip-mode-${key}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
      style={travelMode === key
        ? { backgroundColor: "var(--tpl-accent, #292524)", borderColor: "var(--tpl-accent, #292524)", color: "#fff" }
        : { borderColor: "var(--tpl-line)", color: "var(--tpl-ink, #1a1614)", opacity: 0.75 }
      }
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );

  return (
    <div className="space-y-2 text-[13px]">
      {/* Travel-mode selector */}
      <div className="flex gap-1.5 flex-wrap" data-testid="trip-mode-selector">
        {modeBtn("transit", "Public Transport", Bus)}
        {modeBtn("drive",   "Drive",            Calendar)}
        {modeBtn("walk",    "Walk",             MapPin)}
      </div>

      {/* Booking time + date */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="opacity-70">For booking on</span>
        <input
          type="date"
          value={bookingDate}
          onChange={(e) => setBookingDate(e.target.value)}
          className="px-2 py-0.5 rounded border text-[13px]"
          style={{ borderColor: "var(--tpl-line)" }}
          data-testid="trip-date-input"
        />
        <span className="opacity-70">at</span>
        <input
          type="time"
          value={bookingTime}
          onChange={(e) => setBookingTime(e.target.value)}
          className="px-2 py-0.5 rounded border text-[13px]"
          style={{ borderColor: "var(--tpl-line)" }}
          data-testid="trip-time-input"
        />
        {isPeak && travelMode !== "walk" && (
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                style={{ backgroundColor: "var(--tpl-accent, #292524)", color: "#fff" }}>
            peak
          </span>
        )}
      </div>

      {/* Weather panel */}
      {weather?.source === "open-meteo" && weather.temperature_c != null && (
        <div className="rounded-md p-2 border flex items-center gap-2 text-[12px]"
             style={{ borderColor: "var(--tpl-line)", background: "rgba(0,0,0,0.02)" }}
             data-testid="trip-weather">
          <span className="text-lg leading-none">{weather.icon}</span>
          <div className="flex-1 min-w-0 leading-tight">
            <div><b>{weather.temperature_c}°C</b> · {weather.condition}</div>
            <div className="opacity-70 text-[11px]">
              {weather.precipitation_mm > 0 ? `Rain ${weather.precipitation_mm}mm · ` : ""}
              Wind {weather.wind_kmh} km/h
            </div>
          </div>
          <span className="text-[10px] opacity-60 italic">forecast for booking time</span>
        </div>
      )}

      {/* Leave From Address */}
      <div className="rounded-md p-2 border" style={{ borderColor: "var(--tpl-line)", background: "rgba(0,0,0,0.02)" }} data-testid="leave-from-block">
        <div className="text-[11px] opacity-70 mb-1 font-semibold uppercase tracking-wide">Leave from</div>
        <div className="grid grid-cols-[64px_minmax(0,1fr)] gap-1.5 relative">
          <select
            value={leaveState}
            onChange={(e) => { setLeaveState(e.target.value); setSuburbPicked(null); setSuburbQuery(""); }}
            className="px-1.5 py-1 rounded border text-[12px] uppercase font-bold"
            style={{ borderColor: "var(--tpl-line)" }}
            data-testid="leave-from-state"
          >
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative">
            <input
              type="text"
              placeholder="Suburb (start typing…)"
              value={suburbPicked ? `${suburbPicked.suburb_name} ${suburbPicked.postcode}` : suburbQuery}
              onChange={(e) => { setSuburbPicked(null); setSuburbQuery(e.target.value); setSuburbOpen(true); }}
              onFocus={() => setSuburbOpen(true)}
              onBlur={() => setTimeout(() => setSuburbOpen(false), 200)}
              className="w-full px-2 py-1 rounded border text-[12px]"
              style={{ borderColor: "var(--tpl-line)" }}
              data-testid="leave-from-suburb"
            />
            {suburbOpen && suburbHits.length > 0 && !suburbPicked && (
              <ul className="absolute left-0 right-0 top-full mt-0.5 bg-white rounded-md border shadow-md z-20 max-h-56 overflow-y-auto"
                  style={{ borderColor: "var(--tpl-line)" }} data-testid="leave-from-suburb-hits">
                {suburbHits.map((s, i) => (
                  <li
                    key={`${s.suburb_name}-${s.postcode}-${i}`}
                    onMouseDown={(e) => { e.preventDefault(); setSuburbPicked(s); setSuburbQuery(""); setSuburbOpen(false); }}
                    className="px-2 py-1 text-[12px] cursor-pointer hover:bg-stone-100"
                    data-testid={`leave-from-suburb-hit-${i}`}
                  >
                    <b>{s.suburb_name}</b> <span className="opacity-60">{s.state_code} {s.postcode}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <input
          type="text"
          placeholder="Street address (e.g. 42 Brunswick St)"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          className="mt-1.5 w-full px-2 py-1 rounded border text-[12px]"
          style={{ borderColor: "var(--tpl-line)" }}
          data-testid="leave-from-street"
        />
      </div>

      {/* Transit sub-filter */}
      {travelMode === "transit" && availableTransit.length > 1 && (
        <div className="flex gap-1.5 flex-wrap" data-testid="transit-filter">
          {availableTransit.map((t) => (
            <button
              key={t.key}
              onClick={() => setTransitFilter(t.key)}
              data-testid={`transit-filter-${t.key}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
              style={transitFilter === t.key
                ? { backgroundColor: "var(--tpl-accent, #292524)", borderColor: "var(--tpl-accent, #292524)", color: "#fff" }
                : { borderColor: "var(--tpl-line)", color: "var(--tpl-ink, #1a1614)", opacity: 0.75 }
              }
            >
              {t.icon && <t.icon className="w-3 h-3" />} {t.label} <span style={{ opacity: 0.75 }}>({t.count})</span>
            </button>
          ))}
        </div>
      )}

      {leaveStr && (
        <div className="opacity-90" data-testid="trip-leave-by">
          Leave by <b className="tabular-nums text-base" style={{ color: "var(--tpl-accent)" }}>{leaveStr}</b>
          {journey?.total_min ? <span className="opacity-60 text-[11px] ml-1">({journey.total_min} min total)</span> : null}
          {journey?.transit_mode && travelMode === "transit" && (
            <span className="ml-1.5 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: "var(--tpl-accent, #292524)", color: "#fff" }}
                  data-testid="trip-resolved-mode">
              via {journey.transit_mode}
            </span>
          )}
        </div>
      )}

      {journey?.legs?.length > 0 && (
        <ul className="text-[12px] opacity-85 space-y-0.5 pl-1" data-testid="trip-legs">
          {journey.legs.map((leg, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="tabular-nums text-[11px] font-bold shrink-0"
                    style={{ color: "var(--tpl-accent)" }}>
                {leg.minutes}m
              </span>
              <span className="opacity-90">{leg.detail}</span>
            </li>
          ))}
        </ul>
      )}

      {!leaveStr && !suburbPicked && (
        <div className="text-[12px] opacity-60 italic">
          Pick a suburb above to compute {travelMode === "transit" ? "public transport" : travelMode} time.
        </div>
      )}

      <div className="text-[10px] opacity-50 italic pt-1 border-t" style={{ borderColor: "var(--tpl-line)" }}>
        Estimates — peak/off-peak adjusted, walk legs at ~4.5 km/h. Full TransLink GTFS-RT live-arrivals coming soon.
      </div>
    </div>
  );
}

// ============================================================
// CART PAGE
// ============================================================
function CartPage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  // iter373c patch H — whitelabel-aware URL base.
  const base = isWhitelabel ? "" : `/r/${slug}`;
  const cart = useDemoCart(restaurant.id);
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);

  const gst = cart.subtotal * 0.10;
  const total = cart.subtotal + gst;
  const isClaimed = restaurant.is_claimed;

  const handleCheckout = () => {
    setSubmitted(true);
    setTimeout(() => {
      cart.clear();
      navigate(`${base || "/"}`);
    }, 4000);
  };

  if (submitted) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="cart-success">
        <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
             style={{ backgroundColor: "var(--tpl-accent)", color: "var(--tpl-accent-ink)" }}>
          <ShoppingBag className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
          {isClaimed ? "Order placed!" : "Demo order recorded"}
        </h1>
        <p className="opacity-70 text-[14px]">
          {isClaimed
            ? `${restaurant.name} will prepare your order. Returning home…`
            : "This was a demo order — nothing was actually charged or sent to the restaurant."}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="cart-page">
      <Link to={`${base}/menu`} className="inline-flex items-center gap-1 text-[13px] opacity-70 hover:opacity-100 mb-4">
        ← Back to menu, add more
      </Link>
      <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
        Your order
      </h1>

      {!isClaimed && (
        <div className="rounded-xl p-3 mb-4 bg-amber-50 border border-amber-300 text-amber-900 text-[13px]"
             data-testid="demo-checkout-banner">
          <AlertCircle className="w-4 h-4 inline mr-1.5 align-text-bottom" />
          <b>Demo Checkout</b> — no payment will be processed. To accept real orders,{" "}
          <Link to={`/claim-business?slug=${slug}`} className="underline font-bold">claim this listing</Link>.
        </div>
      )}

      {cart.items.length === 0 ? (
        <div className="text-center py-12 opacity-60" data-testid="cart-empty">
          Your cart is empty.{" "}
          <Link to={`${base}/menu`} className="underline">Browse the menu</Link>.
        </div>
      ) : (
        <>
          <ul className="space-y-2.5 mb-4">
            {cart.items.map((it) => (
              <li key={it.id} className="flex items-center gap-3 rounded-xl p-3 border"
                  style={{ borderColor: "var(--tpl-line)" }} data-testid={`cart-line-${it.id}`}>
                {it.image && (
                  <img src={it.image} alt="" className="w-12 h-12 rounded-md object-cover bg-stone-200" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[14px] capitalize">{it.name}</div>
                  <div className="text-[12px] opacity-65">${fmt(it.price)} each</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => cart.update(it.id, -1)}
                    data-testid={`qty-minus-${it.id}`}
                    className="w-7 h-7 rounded-md border flex items-center justify-center hover:bg-stone-100"
                    style={{ borderColor: "var(--tpl-line)" }}
                  ><Minus className="w-3 h-3" /></button>
                  <span className="font-bold tabular-nums w-6 text-center">{it.qty}</span>
                  <button
                    onClick={() => cart.update(it.id, 1)}
                    data-testid={`qty-plus-${it.id}`}
                    className="w-7 h-7 rounded-md border flex items-center justify-center hover:bg-stone-100"
                    style={{ borderColor: "var(--tpl-line)" }}
                  ><Plus className="w-3 h-3" /></button>
                </div>
                <div className="w-16 text-right font-bold tabular-nums text-[14px]">
                  ${fmt(it.price * it.qty)}
                </div>
                <button
                  onClick={() => cart.remove(it.id)}
                  data-testid={`remove-${it.id}`}
                  className="text-stone-400 hover:text-rose-600"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>

          <div className="rounded-xl p-4 border space-y-1.5 text-[14px]"
               style={{ borderColor: "var(--tpl-line)", backgroundColor: "var(--tpl-surface)" }}>
            <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">${fmt(cart.subtotal)}</span></div>
            <div className="flex justify-between"><span>GST 10%</span><span className="tabular-nums">${fmt(gst)}</span></div>
            <div className="flex justify-between font-bold pt-1.5 border-t" style={{ borderColor: "var(--tpl-line)" }}>
              <span>Total inc GST</span><span className="tabular-nums">${fmt(total)}</span>
            </div>
          </div>

          <button
            onClick={handleCheckout}
            data-testid="checkout-btn"
            className="w-full mt-4 px-4 py-3 rounded-xl text-[14px] font-bold text-white hover:opacity-90"
            style={{ backgroundColor: "var(--tpl-accent, #292524)" }}
          >
            {isClaimed ? `Pay $${fmt(total)} & place order` : `Confirm Demo Order — $${fmt(total)}`}
          </button>
          {!isClaimed && (
            <p className="text-[11px] opacity-60 text-center mt-2">
              No payment will be charged. No order will be sent.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// LOCATION (suburb) PAGE
// ============================================================
// iter373c patch I (2026-09-22) — Renders the SEO/AEO suburb landing
// for the vendor. For restaurant tenants the backend returns competitor
// listings + cuisine focus (legacy). For non-restaurant niches (Tax /
// Legal / Property etc) it returns the vendor's own service pages +
// community/AEO copy for the suburb. This component branches on the
// payload shape so both experiences share one route.
function LocationPage() {
  const { restaurant, slug, isWhitelabel } = useSite();
  const base = isWhitelabel ? "" : `/r/${slug}`;
  const { suburb_slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/directory/restaurants/${slug}/suburb/${suburb_slug}`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData(null));
  }, [slug, suburb_slug]);

  if (!data) return <div className="p-12 text-center opacity-60">Loading…</div>;

  const suburb = data.suburb || (suburb_slug || "").replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const niche = (restaurant?.niche || "").toLowerCase();
  const isRestaurantsNiche = !niche || niche === "restaurants";

  // ── Non-restaurant niches: render a proper suburb landing page ──
  if (!isRestaurantsNiche) {
    const svcPages = data.service_pages || restaurant?.service_pages || [];
    const communityBlurbs = data.community_blurbs || [];
    const aeoQnA = data.aeo_qna || [];
    const vendorName = restaurant?.name || "";
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="location-page">
        <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-1"
           data-testid="location-page-eyebrow">
          Serving {suburb}
        </p>
        <h1 className="text-3xl md:text-4xl font-bold mb-3 leading-tight"
            style={{ fontFamily: "var(--tpl-font-display, serif)" }}
            data-testid="location-page-headline">
          {(restaurant?.niche || "Trusted local expert")} in {suburb}
        </h1>
        <p className="text-[15.5px] leading-relaxed opacity-80 mb-8 max-w-3xl"
           data-testid="location-page-lead">
          {data.lead_paragraph || `${vendorName} looks after businesses, sole traders and families across ${suburb}. Book an appointment, drop into the office minutes away, or ask any tax, bookkeeping or SMSF question — we'll get back to you the same business day.`}
        </p>

        {data.hero_url && (
          <div className="rounded-2xl overflow-hidden mb-8" data-testid="location-page-hero">
            <img src={data.hero_url} alt={`${vendorName} in ${suburb}`}
                 className="w-full h-56 md:h-72 object-cover" />
          </div>
        )}

        {/* Community blurbs — hyper-local SEO/AEO content */}
        {communityBlurbs.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mb-10" data-testid="location-page-community">
            {communityBlurbs.map((b, i) => (
              <div key={i} className="rounded-xl border p-4"
                   style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}>
                <h3 className="font-bold mb-1.5 text-[15px]">{b.title}</h3>
                <p className="text-[13.5px] leading-relaxed opacity-80">{b.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Service page tiles (Tax Returns, Bookkeeping, SMSF, …) */}
        {svcPages.length > 0 && (
          <div className="mb-10" data-testid="location-page-services">
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
              How we help {suburb} businesses & families
            </h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {svcPages.slice(0, 12).map((sp) => (
                <Link key={sp.slug} to={`${base}/business/${sp.slug}`}
                      className="rounded-lg border px-3 py-2 text-[14px] font-semibold hover:border-emerald-600 hover:bg-emerald-50 transition-colors"
                      style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
                      data-testid={`location-svc-${sp.slug}`}>
                  {sp.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* AEO Q&A block for LLM answer engines */}
        {aeoQnA.length > 0 && (
          <div className="mb-10" data-testid="location-page-aeo">
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
              Frequently asked in {suburb}
            </h2>
            <div className="space-y-2">
              {aeoQnA.map((qa, i) => (
                <details key={i} className="rounded-lg border px-4 py-3 group"
                         style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
                         data-testid={`location-qna-${i}`}>
                  <summary className="cursor-pointer font-semibold text-[15px] flex items-center justify-between gap-2">
                    <span>{qa.q}</span>
                    <ChevronDown className="w-4 h-4 shrink-0 transition-transform group-open:rotate-180 opacity-60" />
                  </summary>
                  <p className="mt-2 text-[14px] leading-relaxed opacity-85">{qa.a}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t flex flex-wrap gap-3"
             style={{ borderColor: "var(--tpl-line, #e7e5e4)" }}
             data-testid="location-page-cta">
          <Link to={`${base}/contact#enquiry`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--tpl-accent, #292524)" }}>
            Send an enquiry <ArrowRight className="w-4 h-4" />
          </Link>
          {restaurant?.phone && (
            <a href={`tel:${restaurant.phone}`}
               className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-bold border-2 hover:bg-stone-50"
               style={{ borderColor: "var(--tpl-accent, #292524)", color: "var(--tpl-accent, #292524)" }}>
              <Phone className="w-4 h-4" /> Call {restaurant.phone}
            </a>
          )}
        </div>
      </div>
    );
  }

  // ── Restaurants niche: legacy competitor listing ──
  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="location-page">
      <p className="text-[11px] uppercase tracking-wider opacity-60 font-bold mb-1">
        {data.cuisine_focus} in
      </p>
      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "var(--tpl-font-display, serif)" }}>
        {data.cuisine_focus} restaurants in {suburb}
      </h1>
      <p className="text-[15px] opacity-80 mb-6 max-w-2xl">
        {data.competitor_count} {data.cuisine_focus || "local"} venues in {suburb}. From quick bites to
        sit-down dinners. {data.parent_restaurant?.name} is just minutes away — book a table or order online.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {(data.competitors || []).slice(0, 12).map((r) => (
          <Link key={r.id} to={`/r/${r.slug}`}
                className="rounded-xl p-3 border hover:shadow-md transition"
                style={{ borderColor: "var(--tpl-line)" }}
                data-testid={`competitor-${r.id}`}>
            <div className="flex gap-3">
              {r.logo && <img src={r.logo} alt="" className="w-12 h-12 rounded-lg object-cover bg-stone-200" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] truncate">{r.name}</div>
                <div className="text-[12px] opacity-65 truncate">{r.cuisine} · {r.address}</div>
                {r.rating && (
                  <div className="text-[11px] mt-0.5">
                    <Star className="inline w-3 h-3 fill-amber-400 text-amber-400" /> {r.rating} · {r.review_count || 0}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Suppress unused import lint
void useLocation;
